import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { GlobalService } from '../../../global/services/global.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { AppService } from '../../../app.service';

// Foreign services
import { GhostSystemRunStatusService } from '../../../foreign/services/ghost-system-run-status.service';
import { GhostSystemStatusService } from '../../../foreign/services/ghost-system-status.service';
import { GhostOwnershipInfoService } from '../../../foreign/services/ghost-ownership-info.service';
import { SubPartFService } from '../../../foreign/services/sub-part-f.service';
import { FirGridSaveService } from '../../../foreign/services/fir-grid-save.service';
import { FirGiltiQbaiService } from '../../../foreign/services/fir-gilti-qbai.service';
import { PartnerAllocationsService } from '../../../foreign/services/partner-allocations.service';
import { Fir163jSummaryHoService } from '../../../foreign/services/fir-163j-summary-ho.service';
import { Fir163jSummaryLeMeService } from '../../../foreign/services/fir-163j-summary-le-me.service';
import { FirReviewAutomationService } from '../../../foreign/services/fir-review-automation.service';
import { FirDiagnosticsService } from '../../../foreign/services/fir-diagnostics.service';
import { Fir8858GaapDrilldownService } from '../../../foreign/services/fir-8858-gaap-drilldown.service';
import { DcsGhostSettlementService } from '../../../foreign/components/dcs-ghost-settlement/dcs-ghost-settlement.service';
import { Form5471TextinfoService } from '../../../foreign/components/form5471-textinfo/form5471-textinfo.service';
import { Fir5471ScheduleEService } from '../../../foreign/components/fir-5471-schedule-e/fir-5471-schedule-e.service';
import { Fir5471ScheduleIService } from '../../../foreign/components/fir-5471-schedule-i/fir-5471-schedule-i.service';
import { FirAnalyzeCurrentTaxesService } from '../../../foreign/components/fir-analyze-current-taxes/fir-analyze-current-taxes.service';
import { AddSchdmTemplateIdService } from '../../../foreign/components/add-schdm-template-id/add-schdm-template-id.service';
import { SourcingInputAppBasketService } from '../../../foreign/components/sourcing-input-app-basket/sourcing-input-app-basket.service';
import { FirSanctionedCountriesService } from '../../../foreign/components/fir-sanctioned-countries/fir-sanctioned-countries.service';
import { FirGiltiCalcGridService } from '../../../foreign/components/fir-gilti-calc-grid/fir-gilti-calc-grid.service';
import { FirGiltiConsolidatedGridService } from '../../../foreign/components/fir-gilti-consolidated-grid/fir-gilti-consolidated-grid.service';

// Domestic services
import { WwniM3ReconService } from '../../../domestic/components/wwni-m3-recon/wwni-m3-recon.service';
import { ReviewAdjAutomationService } from '../../../domestic/components/review-adj-automation/review-adj-automation.service';
import { GoldAddressService } from '../../../domestic/components/gold-address/gold-address.service';
import { LockFormService } from '../../../domestic/components/lock-form/lock-form.service';
import { JobDisplayService } from '../../../domestic/components/job-display/job-display.service';
import { AdjOpeningReserveBalancesService } from '../../../domestic/components/adj-opening-reserve-balances/adj-opening-reserve-balances.service';
import { Form1125aService } from '../../../domestic/components/form1125a/form1125a.service';
import { EstSignOffService } from '../../../domestic/components/est-sign-off/est-sign-off.service';
import { DcsDiagnosticsService } from '../../../domestic/components/dcs-diagnostics/dcs-diagnostics.service';
import { Section174Service } from '../../../domestic/components/section174/section174.service';
import { Dashboard901MService } from '../../../domestic/components/dashboard-901m/dashboard-901m.service';
import { SettlementMainService } from '../../../domestic/components/settlement-main/settlement-main.service';
import { F4255PartIService } from '../../../domestic/components/f4255-part-i/f4255-part-i.service';
import { Form851TaxualinfoService } from '../../../domestic/components/form851/form851-taxualinfo/form851-taxualinfo.service';
import { F1120lSchLService } from '../../../F1120/components/f1120l-sch-l/f1120l-sch-l.service';
import { F1120SchJService } from '../../../F1120/components/f1120-sch-j/f1120-sch-j.service';
import { F3800PIIIConsolService } from '../../../F1120/components/f3800-piii-consol/f3800-piii-consol.service';
import { F1120LPCHeadersService } from '../../../Forms/services/f1120-l-pc-headers.service';
import { EfileEditKeyTableDataService } from '../../../efile/edit-key-table-data/efile-edit-key-table-data.service';
import { EfileSubmissionManifestTableService } from '../../../efile/efile-submission-manifest-table/efile-submission-manifest-table.service';
import { EfileEditSubmissionTableDataService } from '../../../efile/edit-submission-table-data/efile-edit-submission-table-data.service';
import { TblMaintenanceService } from '../../../admin/components/tbl-maintenance/tbl-maintenance.service';
import { EfileCtrlFnDispatcherService } from '../../../efile/components/scrubbing-actions/efile-ctrlfn-dispatcher.service';

// Partnership services
import { SourcingAmountsService } from '../../../partnership/services/sourcing-amounts.service';
import { PartnershipM2TaxService } from '../../../partnership/services/partnership-m2-tax.service';
import { PartnershipCapitalAccountsService } from '../../../partnership/services/partnership-capital-accounts.service';
import { PartnershipCapitalAccountsTaxService } from '../../../partnership/services/partnership-capital-accounts-tax.service';
import { Form1065M2Service } from '../../../partnership/components/form1065-m2/form1065-m2.service';
import { FirFdiiQbaiExcludeAcctService } from '../../../ftc/components/fir-fdii-qbai-exclude-acct/fir-fdii-qbai-exclude-acct.service';
import { FirTrueupTaxesService } from '../../../foreign/components/fir-trueup-taxes/fir-trueup-taxes.service';
import { Fir8990ScheduleBService } from '../../../foreign/components/fir-8990-schedule-b/fir-8990-schedule-b.service';

type BuiltInSaveHandler = (callName: string, data: any) => Observable<any> | void;
type RegisterSaveHandlerFn = (callName: string, handler: BuiltInSaveHandler) => void;

@Injectable({
  providedIn: 'root',
})
export class GridSaveBuiltInHandlersService {
  private readonly GENERAL_CONFIG = environment;

  constructor(
    private http: HttpClient,
    private workspaceService: WorkspaceService,
    private globalService: GlobalService,
    private gtwSnackbarService: GtwSnackbarService,
    private ghostSystemRunStatusService: GhostSystemRunStatusService,
    private ghostSystemStatusService: GhostSystemStatusService,
    private ghostOwnershipInfoService: GhostOwnershipInfoService,
    private subPartFService: SubPartFService,
    private firGridSaveService: FirGridSaveService,
    private firGiltiQbaiService: FirGiltiQbaiService,
    private partnerAllocationsService: PartnerAllocationsService,
    private fir163jSummaryHoService: Fir163jSummaryHoService,
    private fir163jSummaryLeMeService: Fir163jSummaryLeMeService,
    private firReviewAutomationService: FirReviewAutomationService,
    private firDiagnosticsService: FirDiagnosticsService,
    private fir8858GaapDrilldownService: Fir8858GaapDrilldownService,
    private dcsGhostSettlementService: DcsGhostSettlementService,
    private form5471TextinfoService: Form5471TextinfoService,
    private fir5471ScheduleEService: Fir5471ScheduleEService,
    private fir5471ScheduleIService: Fir5471ScheduleIService,
    private firAnalyzeCurrentTaxesService: FirAnalyzeCurrentTaxesService,
    private addSchdmTemplateIdService: AddSchdmTemplateIdService,
    private sourcingInputAppBasketService: SourcingInputAppBasketService,
    private firSanctionedCountriesService: FirSanctionedCountriesService,
    private firGiltiCalcGridService: FirGiltiCalcGridService,
    private firGiltiConsolidatedGridService: FirGiltiConsolidatedGridService,
    private wwniM3ReconService: WwniM3ReconService,
    private reviewAdjAutomationService: ReviewAdjAutomationService,
    private goldAddressService: GoldAddressService,
    private lockFormService: LockFormService,
    private jobDisplayService: JobDisplayService,
    private adjOpeningReserveBalancesService: AdjOpeningReserveBalancesService,
    private form1125aService: Form1125aService,
    private estSignOffService: EstSignOffService,
    private dcsDiagnosticsService: DcsDiagnosticsService,
    private section174Service: Section174Service,
    private dashboard901MService: Dashboard901MService,
    private settlementMainService: SettlementMainService,
    private f4255PartIService: F4255PartIService,
    private form851TaxualinfoService: Form851TaxualinfoService,
    private f1120lSchLService: F1120lSchLService,
    private f1120SchJService: F1120SchJService,
    private f3800PIIIConsolService: F3800PIIIConsolService,
    private f1120LPCHeadersService: F1120LPCHeadersService,
    private efileEditKeyTableDataService: EfileEditKeyTableDataService,
    private efileSubmissionManifestTableService: EfileSubmissionManifestTableService,
    private efileEditSubmissionTableDataService: EfileEditSubmissionTableDataService,
    private tblMaintenanceService: TblMaintenanceService,
    private sourcingAmountsService: SourcingAmountsService,
    private partnershipM2TaxService: PartnershipM2TaxService,
    private partnershipCapitalAccountsService: PartnershipCapitalAccountsService,
    private partnershipCapitalAccountsTaxService: PartnershipCapitalAccountsTaxService,
    private form1065M2Service: Form1065M2Service,
    private firFdiiQbaiExcludeAcctService: FirFdiiQbaiExcludeAcctService,
    private firTrueupTaxesService: FirTrueupTaxesService,
    private fir8990ScheduleBService: Fir8990ScheduleBService,
    private appService: AppService,
    private efileCtrlFnDispatcher: EfileCtrlFnDispatcherService,
  ) {}

  private get ssoId(): string {
    return this.appService.USER_SETTINGS?.user?.sso_id || '';
  }

  registerBuiltInHandlers(registerSaveHandler: RegisterSaveHandlerFn): void {
    registerSaveHandler('save_entity_lock', (_callName: string, data: any) => {
      return this.handleEntityLockSave(data);
    });

    // Ghost / foreign
    registerSaveHandler('save_ghost_run_status', (_c, d) => this.handleGhostRunStatusSave(d));
    registerSaveHandler('save_ghost_status', (_c, d) => this.handleGhostStatusSave(d));
    registerSaveHandler('save_ghost_ownership', (_c, d) => this.handleGhostOwnershipSave(d));
    registerSaveHandler('update_ghost_settlement', (_c, d) => this.handleUpdateGhostSettlement(d));

    // Sub Part F
    registerSaveHandler('save_subPart_F', (_c, d) => this.handleSubPartFSave(d));
    registerSaveHandler('saveCehckboxDetails', (_c, d) => this.handleSubPartFDetailsSave(d));
    registerSaveHandler('saveBasketValue', (_c, d) => this.handleSubPartFDetailsSave(d));

    // FIR grid save
    registerSaveHandler('save_htie_new_election', (_c, d) => this.handleHtieNewElectionSave(d));
    registerSaveHandler('save_htie_passive_new_election', (_c, d) => this.handleHtiePassiveNewElectionSave(d));

    // GILTI / QBAI
    registerSaveHandler('saveqbaiExternalAmts', (_c, d) => this.handleQbaiExternalAmtsSave(d));
    registerSaveHandler('saveExternalQuarterAmounts', (_c, d) => this.handleExternalQuarterAmountsSave(d));

    // 163j
    registerSaveHandler('save_163j_Line_amounts', (_c, d) => this.handle163jLineAmountsSave(d));
    registerSaveHandler('save_163j_Enable', (_c, d) => this.handle163jEnableSave(d));

    // 5471 schedules
    registerSaveHandler('save_schedule_G', (_c, d) => this.handleScheduleGSave(d));
    registerSaveHandler('saveSchedule5471SchdE', (_c, d) => this.handleSchedule5471SchdESave(d));

    // FIR automation / diagnostics
    registerSaveHandler('firReviewAutoCehckbox', (_c, d) => this.handleFirReviewAutoCheckboxSave(d));
    registerSaveHandler('saveFirDiagnosticsAction', (_c, d) => this.handleFirDiagnosticsActionSave(d));

    // Partner / partnership
    registerSaveHandler('save_Allocation_Amounts', (_c, d) => this.handleAllocationAmountsSave(d));
    registerSaveHandler('save_Sourcing_Amounts', (_c, d) => this.handleSourcingAmountsSave(d));
    registerSaveHandler('save_Capital_Amounts', (_c, d) => this.handleCapitalAmountsSave(d));
    registerSaveHandler('save_Capital_Amounts_Tax', (_c, d) => this.handleCapitalAmountsTaxSave(d));
    registerSaveHandler('save_M2_Amounts', (_c, d) => this.handleM2AmountsSave(d));
    registerSaveHandler('save_M2_Tax_Amounts', (_c, d) => this.handleM2TaxAmountsSave(d));

    // Domestic
    registerSaveHandler('save_fir_wwni_m3_comments', (_c, d) => this.handleWwniM3CommentsSave(d));
    registerSaveHandler('lock_form_save', (_c, d) => this.handleLockFormSave(d));
    registerSaveHandler('job_display', (_c, d) => this.handleJobDisplaySave(d));
    registerSaveHandler('save_local_purchase_amount', (_c, d) => this.handleLocalPurchaseAmountSave(d));
    registerSaveHandler('save_1125_amount', (_c, d) => this.handle1125AmountSave(d));
    registerSaveHandler('EST_SignOff', (_c, d) => this.handleEstSignOffSave(d));
    registerSaveHandler('save_dcs_Diagnostics', (_c, d) => this.handleDcsDiagnosticsSave(d));

    // Remaining Gridsave.xlsx entries (explicit callback ownership; pending service migration)
    registerSaveHandler('Activate Statements', (_c, d) => this.handleActivateStatementsSave(d));
    registerSaveHandler('Admin Data Services', (_c, d) => this.handleAdminDataServicesSave(d));
    registerSaveHandler('Application Messages', (_c, d) => this.handleApplicationMessagesSave(d));
    registerSaveHandler('BEPS PARAM', (_c, d) => this.handleBEPSPARAMSave(d));
    registerSaveHandler('BU_sign_off', (_c, d) => this.handleBUSignOffSave(d));
    registerSaveHandler('Basis', (_c, d) => this.handleBasisSave(d));
    registerSaveHandler('CbCR Adjustments - Data Entry', (_c, d) => this.handleCbCRAdjustmentsDataEntrySave(d));
    registerSaveHandler('Currency', (_c, d) => this.handleCurrencySave(d));
    registerSaveHandler('Edit Key Table', (_c, d) => this.handleEditKeyTableSave(d));
    registerSaveHandler('Edit Submission Manifest Table', (_c, d) => this.handleEditSubmissionManifestTableSave(d));
    registerSaveHandler('Edit Submission Table', (_c, d) => this.handleEditSubmissionTableSave(d));
    registerSaveHandler('PDF DD', (_c, d) => this.handlePDFDDSave(d));
    registerSaveHandler('PDF FormBucket', (_c, d) => this.handlePDFFormBucketSave(d));
    registerSaveHandler('PDF Repeated Data', (_c, d) => this.handlePDFRepeatedDataSave(d));
    registerSaveHandler('PDF S3', (_c, d) => this.handlePDFS3Save(d));
    registerSaveHandler('Project Data', (_c, d) => this.handleProjectDataSave(d));
    registerSaveHandler('Project Step Data', (_c, d) => this.handleProjectStepDataSave(d));
    registerSaveHandler('Return Period', (_c, d) => this.handleReturnPeriodSave(d));
    registerSaveHandler('Set As Filed/Enable QAR', (_c, d) => this.handleSetAsFiledEnableQARSave(d));
    registerSaveHandler('Settlement_DCS_Main', (_c, d) => this.handleSettlementDCSMainSave(d));
    registerSaveHandler('Stmt Ignore Reasons', (_c, d) => this.handleStmtIgnoreReasonsSave(d));
    registerSaveHandler('Stock Type', (_c, d) => this.handleStockTypeSave(d));
    registerSaveHandler('TBL_3_EFILE', (_c, d) => this.handleTBL3EFILESave(d));
    registerSaveHandler('TBL_3_JCD', (_c, d) => this.handleTBL3JCDSave(d));
    registerSaveHandler('beps_cash_taxes_signoff', (_c, d) => this.handleBepsCashTaxesSignoffSave(d));
    registerSaveHandler('beps_cat_check', (_c, d) => this.handleBepsCatCheckSave(d));
    registerSaveHandler('beps_comment_save', (_c, d) => this.handleBepsCommentSaveSave(d));
    registerSaveHandler('beps_country_parent_le', (_c, d) => this.handleBepsCountryParentLeSave(d));
    registerSaveHandler('beps_country_signoff', (_c, d) => this.handleBepsCountrySignoffSave(d));
    registerSaveHandler('beps_master_lock', (_c, d) => this.handleBepsMasterLockSave(d));
    registerSaveHandler('beps_me_lock', (_c, d) => this.handleBepsMeLockSave(d));
    registerSaveHandler('calculationGridInputs', (_c, d) => this.handleCalculationGridInputsSave(d));
    registerSaveHandler('callSection174', (_c, d) => this.handleCallSection174Save(d));
    registerSaveHandler('delete_ins_acct_record', (_c, d) => this.handleDeleteInsAcctRecordSave(d));
    registerSaveHandler('enable_group_notification', (_c, d) => this.handleEnableGroupNotificationSave(d));
    registerSaveHandler('goldLegalEntityName', (_c, d) => this.handleGoldLegalEntityNameSave(d));
    registerSaveHandler('post_901_adjustments', (_c, d) => this.handlePost901AdjustmentsSave(d));
    registerSaveHandler('pship_partner_163j_schd_a', (_c, d) => this.handlePshipPartner163jSchdASave(d));
    registerSaveHandler('ptp_sourcing', (_c, d) => this.handlePtpSourcingSave(d));
    registerSaveHandler('reviewAutoCheckbox', (_c, d) => this.handleReviewAutoCheckboxSave(d));
    registerSaveHandler('saveComment', (_c, d) => this.handleSaveCommentSave(d));
    registerSaveHandler('savePyCyComment', (_c, d) =>
      this.efileCtrlFnDispatcher.saveCommentFromGridInput(d, 'pycy'),
    );
    registerSaveHandler('saveEfileDiagComment', (_c, d) =>
      this.efileCtrlFnDispatcher.saveCommentFromGridInput(d, 'diag'),
    );
    registerSaveHandler('saveFdiiQbaiAcctExclude', (_c, d) => this.handleSaveFdiiQbaiAcctExcludeSave(d));
    registerSaveHandler('saveFdiiQbaiAmt', (_c, d) => this.handleSaveFdiiQbaiAmtSave(d));
    registerSaveHandler('saveFdiiQbaiPtrAmt', (_c, d) => this.handleSaveFdiiQbaiPtrAmtSave(d));
    registerSaveHandler('saveGILTIHTEAnalysisOverride', (_c, d) => this.handleSaveGILTIHTEAnalysisOverrideSave(d));
    registerSaveHandler('saveTBBSDiagnostics', (_c, d) => this.handleSaveTBBSDiagnosticsSave(d));
    registerSaveHandler('saveTestedUnits', (_c, d) => this.handleSaveTestedUnitsSave(d));
    registerSaveHandler('save_851_form_data', (_c, d) => this.handleSave851FormDataSave(d));
    registerSaveHandler('save_8990_schedule_B_amounts', (_c, d) => this.handleSave8990ScheduleBAmountsSave(d));
    registerSaveHandler('save_901_adjustments', (_c, d) => this.handleSave901AdjustmentsSave(d));
    registerSaveHandler('save_Amount_Data', (_c, d) => this.handleSaveAmountDataSave(d));
    registerSaveHandler('save_Cash_Payment_Flag', (_c, d) => this.handleSaveCashPaymentFlagSave(d));
    registerSaveHandler('save_Credit_Facility_Data', (_c, d) => this.handleSaveCreditFacilityDataSave(d));
    registerSaveHandler('save_Current_Signoff', (_c, d) => this.handleSaveCurrentSignoffSave(d));
    registerSaveHandler('save_F1120L_Schd_A_details', (_c, d) => this.handleSaveF1120LSchdADetailsSave(d));
    registerSaveHandler('save_F1120L_Schd_L_details', (_c, d) => this.handleSaveF1120LSchdLDetailsSave(d));
    registerSaveHandler('save_F1120PC_Schd_C_details', (_c, d) => this.handleSaveF1120PCSchdCDetailsSave(d));
    registerSaveHandler('save_F1120_L_PC_header', (_c, d) => this.handleSaveF1120LPCHeaderSave(d));
    registerSaveHandler('save_F1120_Schd_C_details', (_c, d) => this.handleSaveF1120SchdCDetailsSave(d));
    registerSaveHandler('save_F3800PIII_Consol_Amounts', (_c, d) => this.handleSaveF3800PIIIConsolAmountsSave(d));
    registerSaveHandler('save_Ins_Sch_P_details', (_c, d) => this.handleSaveInsSchPDetailsSave(d));
    registerSaveHandler('save_Non_Financial', (_c, d) => this.handleSaveNonFinancialSave(d));
    registerSaveHandler('save_Sanctioned_countries', (_c, d) => this.handleSaveSanctionedCountriesSave(d));
    registerSaveHandler('save_TaxTrueUp_Signoff', (_c, d) => this.handleSaveTaxTrueUpSignoffSave(d));
    registerSaveHandler('save_adjustments_details', (_c, d) => this.handleSaveAdjustmentsDetailsSave(d));
    registerSaveHandler('save_amt_paid', (_c, d) => this.handleSaveAmtPaidSave(d));
    registerSaveHandler('save_amt_received_in_ho', (_c, d) => this.handleSaveAmtReceivedInHoSave(d));
    registerSaveHandler('save_cbcr_variance_type', (_c, d) => this.handleSaveCbcrVarianceTypeSave(d));
    registerSaveHandler('save_comment_header', (_c, d) => this.handleSaveCommentHeaderSave(d));
    registerSaveHandler('save_dcl_details', (_c, d) => this.handleSaveDclDetailsSave(d));
    registerSaveHandler('save_dcl_trans_details', (_c, d) => this.handleSaveDclTransDetailsSave(d));
    registerSaveHandler('save_elimination_amount', (_c, d) => this.handleSaveEliminationAmountSave(d));
    registerSaveHandler('save_ghost_scenario_adjustments', (_c, d) => this.handleSaveGhostScenarioAdjustmentsSave(d));
    registerSaveHandler('save_ghost_schdm_exclude', (_c, d) => this.handleSaveGhostSchdmExcludeSave(d));
    registerSaveHandler('save_ghost_sourcing_basket_data', (_c, d) => this.handleSaveGhostSourcingBasketDataSave(d));
    registerSaveHandler('save_gilti_calc', (_c, d) => this.handleSaveGiltiCalcSave(d));
    registerSaveHandler('save_gilti_consolidated_amts', (_c, d) => this.handleSaveGiltiConsolidatedAmtsSave(d));
    registerSaveHandler('save_illegal_payments', (_c, d) => this.handleSaveIllegalPaymentsSave(d));
    registerSaveHandler('save_include_Flag', (_c, d) => this.handleSaveIncludeFlagSave(d));
    registerSaveHandler('save_local_acct_desc', (_c, d) => this.handleSaveLocalAcctDescSave(d));
    registerSaveHandler('save_local_prior_year_amt', (_c, d) => this.handleSaveLocalPriorYearAmtSave(d));
    registerSaveHandler('save_max_loan_user_amt', (_c, d) => this.handleSaveMaxLoanUserAmtSave(d));
    registerSaveHandler('save_qre_comments', (_c, d) => this.handleSaveQreCommentsSave(d));
    registerSaveHandler('save_qre_credit_additional_dtls', (_c, d) => this.handleSaveQreCreditAdditionalDtlsSave(d));
    registerSaveHandler('save_qre_credit_allocation', (_c, d) => this.handleSaveQreCreditAllocationSave(d));
    registerSaveHandler('save_qre_sign_Off', (_c, d) => this.handleSaveQreSignOffSave(d));
    registerSaveHandler('save_qre_yoy_signoff_dtls', (_c, d) => this.handleSaveQreYoySignoffDtlsSave(d));
    registerSaveHandler('save_schJ_consol_amounts', (_c, d) => this.handleSaveSchJConsolAmountsSave(d));
    registerSaveHandler('save_schd_I_grid_data', (_c, d) => this.handleSaveSchdIGridDataSave(d));
    registerSaveHandler('save_schd_o', (_c, d) => this.handleSaveSchdOSave(d));
    registerSaveHandler('save_schd_o_enabled', (_c, d) => this.handleSaveSchdOEnabledSave(d));
    registerSaveHandler('save_schd_p_enabled', (_c, d) => this.handleSaveSchdPEnabledSave(d));
    registerSaveHandler('save_section174_Comments', (_c, d) => this.handleSaveSection174CommentsSave(d));
    registerSaveHandler('save_stmt_flag_checked', (_c, d) => this.handleSaveStmtFlagCheckedSave(d));
    registerSaveHandler('save_suupplement_details', (_c, d) => this.handleSaveSuupplementDetailsSave(d));
    registerSaveHandler('save_table_maintanance', (_c, d) => this.handleSaveTableMaintananceSave(d));
    registerSaveHandler('save_tax_amt_Flag', (_c, d) => this.handleSaveTaxAmtFlagSave(d));
    registerSaveHandler('save_wwni_asset_data', (_c, d) => this.handleSaveWwniAssetDataSave(d));
    registerSaveHandler('save_wwni_comm_data', (_c, d) => this.handleSaveWwniCommDataSave(d));
    registerSaveHandler('schdmselector', (_c, d) => this.handleSchdmselectorSave(d));
    registerSaveHandler('settlement_bus_signoff', (_c, d) => this.handleSettlementBusSignoffSave(d));
    registerSaveHandler('settlement_corp_signoff', (_c, d) => this.handleSettlementCorpSignoffSave(d));
    registerSaveHandler('special_Alloc_Override', (_c, d) => this.handleSpecialAllocOverrideSave(d));
    registerSaveHandler('stir_sd_code_save', (_c, d) => this.handleStirSdCodeSaveSave(d));
    registerSaveHandler('stir_settlement_entity_admin', (_c, d) => this.handleStirSettlementEntityAdminSave(d));
    registerSaveHandler('stir_settlement_target_date', (_c, d) => this.handleStirSettlementTargetDateSave(d));
    registerSaveHandler('submission_signoff', (_c, d) => this.handleSubmissionSignoffSave(d));
    registerSaveHandler('tb_import_from_fdm_alert', (_c, d) => this.handleTbImportFromFdmAlertSave(d));
    registerSaveHandler('toggle-change-log', (_c, d) => this.handleToggleChangeLogSave(d));
    registerSaveHandler('toggle_beps_variance', (_c, d) => this.handleToggleBepsVarianceSave(d));
    registerSaveHandler('Refresh Materialized Views', (_c, d) => this.handleRefreshMaterializedViewsSave(d));
  }

  private getCbcActionUrl(actionIdFor30: string, actionIdFor5606: string): string {
    const activityKey = String(this.workspaceService?.workspaceFactory?.activeScreen?.activity_key || '');
    const actionId = activityKey === '5606' ? actionIdFor5606 : actionIdFor30;
    return `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_id=${actionId}`;
  }

  private handleActivateStatementsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const globalParams = globalContext?.globalParams || {};
    const rowData = data?.rowData || {};
    const scenario = filterParams?.scenario ?? globalParams?.scenario;

    // Without a statement id the backend proc raises NO_DATA_FOUND, which
    // surfaces as the opaque "setStatementActive: 100". Fail loudly instead.
    if (rowData.STATEMENT_ID === null || rowData.STATEMENT_ID === undefined || rowData.STATEMENT_ID === '') {
      this.gtwSnackbarService.openSnackBar(
        'Cannot update this statement: the row is missing a statement id.',
        'gtw-snackbar--yellow'
      );
      return new Observable(sub => sub.next({ skipped: true }));
    }

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=w1g6nw`,
        {
          // Legacy DSTStmtFactory.ActivateStmt went through
          // JsonObjectFactory.saveJSON, which appended the screen context and a
          // lock-check object to every payload; /saveJsonObject needs both.
          ...globalParams,
          ...filterParams,
          checkLockedbj: JSON.stringify([{ scenario_key: Number(scenario) || 0 }]),
          sso_id: this.ssoId,
          statement_id: rowData.STATEMENT_ID,
          is_active: rowData.IS_ACTIVE
        },
        { withCredentials: true }
      )
    ).pipe(
      tap((response) => {
        if (!response?.skipped) {
          // Parity with the legacy `$rootScope.$emit('gridUpdate', {})`.
          this.refreshActiveScreenGrid();
        }
      })
    );
  }

  private handleAdminDataServicesSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=294ju1`,
        {
          sso_id: this.ssoId,
          mview_name: rowData.OBJECT_NAME
        },
        { withCredentials: true }
      ),
      'MVIEW Refreshed Successfully.',
      'Error occurred while refreshing MVIEW'
    ).pipe(
      tap((response) => {
        if (!response?.skipped) {
          // Legacy DSTAdminRefreshFactory emitted `gridUpdate` after
          // action_code=294ju1. Trigger active-screen grid reload for parity.
          this.refreshActiveScreenGrid();
        }
      })
    );
  }

  private handleRefreshMaterializedViewsSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=bdi673`,
        {
          sso_id: this.ssoId,
          schema: rowData.SCHEMA_NAME,
          mview_name: rowData.OBJECT_NAME
        },
        { withCredentials: true }
      ),
      'MVIEW refresh queued successfully.',
      'An error occurred while queueing the MVIEW refresh.'
    ).pipe(
      tap((response) => {
        if (!response?.skipped) {
          this.refreshActiveScreenGrid();
        }
      })
    );
  }

  private handleApplicationMessagesSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=e03l7i`,
        {
          sso_id: this.ssoId,
          message_id: rowData.MESSAGEID,
          message_name: rowData.MESSAGEDETAILS,
          message_desc: rowData.MESSAGETYPE,
          is_active: rowData.ISACTIVE,
          crudflag: 'U'
        },
        { withCredentials: true }
      )
    );
  }

  private handleBEPSPARAMSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const globalParams = globalContext?.globalParams || {};
    const payload = {
      sso_id: this.ssoId,
      clob_data: JSON.stringify([rowData]),
      tax_year: globalParams.tax_year || filterParams?.tax_year,
      clob_settings: JSON.stringify([{ screen_key: globalParams.screen_key }])
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=f1ah9x`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleBUSignOffSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.settlementMainService.buSignOff(
        this.GENERAL_CONFIG.base_url,
        data?.rowData || {},
        globalContext?.globalParams || {}
      )
    );
  }

  private handleBasisSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=fdhs3s`,
        {
          sso_id: this.ssoId,
          basis_name: rowData.BASIS_NAME,
          basis_id: rowData.BASIS_ID,
          is_active: rowData.IS_ACTIVE,
          crudflag: 'U'
        },
        { withCredentials: true }
      )
    );
  }

  private handleCbCRAdjustmentsDataEntrySave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colData = data?.colData || {};
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      process_name: 'CbCr_Adjustments_Data_Entry',
      clob_data: JSON.stringify([
        {
          ...rowData,
          edited_column: colData.map,
          edited_value: rowData?.[colData.map]
        }
      ]),
      sso_id: this.ssoId,
      scenario: filterParams?.scenario
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=guepv1`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleCurrencySave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      sso_id: this.ssoId,
      currency_name: rowData.CURRENCY_NAME,
      currency_code: rowData.CURRENCY_CODE,
      currency_id: rowData.CURRENCY_ID,
      is_active: rowData.IS_ACTIVE,
      crudflag: 'U'
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=mxrfvu`,
        payload,
        { withCredentials: true }
      ),
      'Data saved Successfully.',
      'Error occurred while saving Currency'
    );
  }

  private handleEditKeyTableSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const saveUrl = data?.baseUrl || data?.url || `${this.GENERAL_CONFIG.base_url}/saveJsonObject`;
    const params = data?.params || data?.payload || {};
    return this.wrapSave(
      this.efileEditKeyTableDataService.updateKeyTable(
        saveUrl,
        params,
        globalContext,
        filterParams,
        data?.rowData || {}
      )
    );
  }

  private handleEditSubmissionManifestTableSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const saveUrl = data?.baseUrl || data?.url || `${this.GENERAL_CONFIG.base_url}/saveJsonObject`;
    const params = data?.params || data?.payload || {};
    const isDelete = Boolean(data?.isDelete) || String(data?.action || '').toLowerCase().includes('delete');
    const request$ = isDelete
      ? this.efileSubmissionManifestTableService.deleteSubManifestRow(
          saveUrl,
          params,
          globalContext,
          filterParams,
          data?.rowData || {}
        )
      : this.efileSubmissionManifestTableService.updateSunMnfstTable(
          saveUrl,
          params,
          globalContext,
          filterParams,
          data?.rowData || {}
        );
    return this.wrapSave(request$);
  }

  private handleEditSubmissionTableSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const saveUrl = data?.baseUrl || data?.url || `${this.GENERAL_CONFIG.base_url}/saveJsonObject`;
    const params = data?.params || data?.payload || {};
    return this.wrapSave(
      this.efileEditSubmissionTableDataService.updateSbmsnTable(
        saveUrl,
        params,
        globalContext,
        filterParams,
        data?.rowData || {}
      )
    );
  }

  private handlePDFDDSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      process_name: 'UPDATE_PDF_DD_DATA',
      sso_id: this.ssoId,
      clob_data: JSON.stringify([data?.rowData || {}])
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=rlgql2`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handlePDFFormBucketSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      process_name: 'UPDATE_FORM_BUCKET_DATA',
      sso_id: this.ssoId,
      clob_data: JSON.stringify([data?.rowData || {}])
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=0klmyf`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handlePDFRepeatedDataSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      process_name: 'UPDATE_REPEATED_DATA',
      sso_id: this.ssoId,
      clob_data: JSON.stringify([data?.rowData || {}])
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=f21qk2`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handlePDFS3Save(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      process_name: 'UPDATE_S3_DATA',
      sso_id: this.ssoId,
      clob_data: JSON.stringify([data?.rowData || {}])
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=6odqlj`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleProjectDataSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      process_name: 'UPDATE_PROJECT_DATA',
      sso_id: this.ssoId,
      clob_data: JSON.stringify([data?.rowData || {}])
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=3lwv34`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleProjectStepDataSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      process_name: 'UPDATE_PROJECT_STEP_DATA',
      sso_id: this.ssoId,
      clob_data: JSON.stringify([data?.rowData || {}])
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=xnged6`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleReturnPeriodSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=nj3taj`,
        {
          sso_id: this.ssoId,
          retpd_desc: rowData.RETURN_TYPE_DESCRIPTION,
          retpd_id: rowData.RETURN_PERIOD_ID,
          is_active: rowData.IS_ACTIVE,
          crudflag: 'U'
        },
        { withCredentials: true }
      )
    );
  }

  private handleSetAsFiledEnableQARSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=38x972`,
        {
          sso_id: this.ssoId,
          tax_year: filterParams?.tax_year || globalContext?.globalParams?.tax_year,
          dcn_id: rowData.DCNID,
          filed_status_id: rowData.FILED_STATUS_ID
        },
        { withCredentials: true }
      ),
      'Data Saved Successfully.',
      'Error occurred while saving filed status'
    );
  }

  private handleSettlementDCSMainSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.settlementMainService.saveSignOff(
        this.GENERAL_CONFIG.base_url,
        data?.rowData || {},
        globalContext?.globalParams || {}
      )
    );
  }

  private handleStmtIgnoreReasonsSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=ya0rnu`,
        {
          sso_id: this.ssoId,
          reason_id: rowData.REASONID,
          reason_name: rowData.REASON,
          reason_desc: rowData.REASONDESC,
          is_active: rowData.IS_ACTIVE,
          crudflag: 'U'
        },
        { withCredentials: true }
      )
    );
  }

  private handleStockTypeSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=6uvsff`,
        {
          sso_id: this.ssoId,
          stktype_name: rowData.STOCK_TYPE_NAME,
          stktype_id: rowData.STOCK_TYPE_ID,
          is_active: rowData.IS_ACTIVE,
          crudflag: 'U'
        },
        { withCredentials: true }
      )
    );
  }

  private handleTBL3EFILESave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=79vvlx`,
        {
          sso_id: this.ssoId,
          tax_year: filterParams?.tax_year,
          scenario: filterParams?.scenario,
          activate: rowData.ACTIVATE === 1 ? 'Y' : 'N',
          sno: rowData.SNO
        },
        { withCredentials: true }
      )
    );
  }

  private handleTBL3JCDSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=ipa6up`,
        {
          sso_id: this.ssoId,
          tax_year: filterParams?.tax_year,
          scenario: filterParams?.scenario,
          all_tax_jcd: rowData.APPLY_TO_ALL_TAX_JURISDICTIONS === 1 ? 'Y' : 'N',
          sno: rowData.SNO
        },
        { withCredentials: true }
      )
    );
  }

  private handleBepsCashTaxesSignoffSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      beps_pk: rowData.object_id,
      cash_taxes: rowData.Cash_Taxes
    };
    return this.wrapSave(
      this.http.post<any>(
        this.getCbcActionUrl('96', '31068'),
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleBepsCatCheckSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colData = data?.colData || {};
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      beps_pk: rowData.object_id,
      checked: rowData?.[colData.map],
      cat_desc: colData.map
    };
    return this.wrapSave(
      this.http.post<any>(
        this.getCbcActionUrl('59', '31071'),
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleBepsCommentSaveSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const hasTbl1Keys = rowData.MARS_ID && rowData.GOLD_ID;
    const payload = hasTbl1Keys
      ? {
          ...globalContext?.globalParams,
          ...filterParams,
          country_code: rowData.LE_Country_Code,
          comments: rowData.COMMENTS,
          mars_id: rowData.MARS_ID,
          gold_id: rowData.GOLD_ID
        }
      : {
          ...globalContext?.globalParams,
          ...filterParams,
          object_id: rowData.object_id,
          comments: rowData.Comments
        };
    return this.wrapSave(
      this.http.post<any>(
        hasTbl1Keys ? this.getCbcActionUrl('57', '31069') : this.getCbcActionUrl('58', '31070'),
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleBepsCountryParentLeSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      parent_le: rowData.Parent_LE,
      beps_pk: rowData.object_id
    };
    return this.wrapSave(
      this.http.post<any>(
        this.getCbcActionUrl('72', '31073'),
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleBepsCountrySignoffSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      notified: rowData.Notified,
      beps_pk: rowData.object_id
    };
    return this.wrapSave(
      this.http.post<any>(
        this.getCbcActionUrl('71', '31072'),
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleBepsMasterLockSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=kt0xfz`,
        {
          sso_id: this.ssoId,
          tax_year: filterParams?.tax_year,
          scenario: filterParams?.scenario,
          me_code: rowData.MAINTENANCE_OWNER,
          master_lock_flag: rowData.MASTER_LOCK_Y_N === 1 ? 'Y' : 'N'
        },
        { withCredentials: true }
      )
    );
  }

  private handleBepsMeLockSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=wgn1de`,
        {
          sso_id: this.ssoId,
          tax_year: filterParams?.tax_year,
          scenario: filterParams?.scenario,
          me_code: rowData.MAINTENANCE_OWNER,
          tb_lock_flag: rowData.TB_LOCK_Y_N === 1 ? 'Y' : 'N',
          adj_lock_flag: rowData.ADJ_LOCK_Y_N === 1 ? 'Y' : 'N',
          comments: rowData.COMMENTS
        },
        { withCredentials: true }
      )
    );
  }

  private handleCalculationGridInputsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colData = data?.colData || { map: data?.col_id };
    return this.wrapSave(
      this.firGiltiCalcGridService.calculationGridInputs(
        this.GENERAL_CONFIG.base_url,
        rowData,
        colData,
        globalContext,
        filterParams
      )
    );
  }

  private handleCallSection174Save(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.section174Service.getSectionDetails(
        this.GENERAL_CONFIG.base_url,
        data,
        globalContext,
        filterParams
      )
    );
  }

  private handleDeleteInsAcctRecordSave(data: any): Observable<any> {
    const rowData = data?.rowData || {};
    const payload = {
      tax_year: this.globalService?.global?.globalParams?.tax_year || rowData.TAX_YEAR || '',
      process_name: 'DELETE_ACCOUNT_MAPPING',
      clob_data: JSON.stringify([rowData]),
      clob_setting: JSON.stringify({
        tax_year: this.globalService?.global?.globalParams?.tax_year || rowData.TAX_YEAR || '',
        scenario: this.globalService?.global?.globalParams?.scenario || rowData.SCENARIO || '',
        jcd_key: this.globalService?.global?.globalParams?.jcd_key || rowData.JCD_KEY || 250
      })
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=wbv647`,
        payload,
        { withCredentials: true }
      ),
      'Data deleted Successfully.',
      'Error occurred while deleting account mapping'
    );
  }

  private handleEnableGroupNotificationSave(data: any): Observable<any> {
    const rowData = data?.rowData || {};
    const payload = {
      tax_year: rowData.TAX_YEAR,
      scenario: rowData.SCENARIO,
      group_key: rowData.MEF_GROUP_KEY,
      jcd_key: this.globalService?.global?.globalParams?.jcd_key || rowData.JCD_KEY || 250,
      enable: rowData.ENABLE_FOR_DIAG_NOTIFICATION
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_id=32951`,
        payload,
        { withCredentials: true }
      ),
      'Succesfully updated group for notification.',
      'Error occurred while updating group notification'
    );
  }

  private handleGoldLegalEntityNameSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.goldAddressService.saveChange(
        this.GENERAL_CONFIG.base_url,
        rowData,
        Number(rowData?.ADDRESS_ID || 0),
        globalContext,
        filterParams
      ),
      'Entity Name changed Successfully.',
      'An error has occurred. If this error continues please go to the help page and contact support'
    );
  }

  private handlePost901AdjustmentsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colData = data?.colData || {};
    const comments = data?.COMMENTS || rowData?.COMMENTS || rowData?.comment || '';
    return this.wrapSave(
      this.dashboard901MService.save901Adjustment(
        this.GENERAL_CONFIG.base_url,
        rowData,
        colData,
        comments,
        globalContext,
        filterParams
      )
    );
  }

  private handlePshipPartner163jSchdASave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const ln43e = Number(rowData.LN_43C || 0) + Number(rowData.LN_43D || 0);
    const ln43fg = Number(rowData.LN_43F || 0) + Number(rowData.LN_43G || 0);
    const ln43h = Math.min(ln43e, ln43fg);
    const ln43i = ln43e - ln43h;

    const jsonObj = [
      { amount: rowData.LN_43C, acct_key: rowData.LN_43C_TO_ACCT, pship_combination_key: rowData.PSHIP_COMBINATION_KEY, ptr_combination_key: rowData.PTR_COMBINATION_KEY },
      { amount: rowData.LN_43D, acct_key: rowData.LN_43D_TO_ACCT, pship_combination_key: rowData.PSHIP_COMBINATION_KEY, ptr_combination_key: rowData.PTR_COMBINATION_KEY },
      { amount: rowData.LN_43F, acct_key: rowData.LN_43F_TO_ACCT, pship_combination_key: rowData.PSHIP_COMBINATION_KEY, ptr_combination_key: rowData.PTR_COMBINATION_KEY },
      { amount: rowData.LN_43G, acct_key: rowData.LN_43G_TO_ACCT, pship_combination_key: rowData.PSHIP_COMBINATION_KEY, ptr_combination_key: rowData.PTR_COMBINATION_KEY },
      { amount: ln43e, acct_key: rowData.LN_43E_TO_ACCT, pship_combination_key: rowData.PSHIP_COMBINATION_KEY, ptr_combination_key: rowData.PTR_COMBINATION_KEY },
      { amount: ln43h, acct_key: rowData.LN_43H_TO_ACCT, pship_combination_key: rowData.PSHIP_COMBINATION_KEY, ptr_combination_key: rowData.PTR_COMBINATION_KEY },
      { amount: ln43i, acct_key: rowData.LN_43I_TO_ACCT, pship_combination_key: rowData.PSHIP_COMBINATION_KEY, ptr_combination_key: rowData.PTR_COMBINATION_KEY }
    ];
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      jsonSettings: JSON.stringify([{
        tax_year: filterParams?.tax_year || globalContext?.globalParams?.tax_year,
        group_trans_key: rowData.GROUP_TRANS_KEY
      }]),
      jsonObj: JSON.stringify(jsonObj)
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=rcbfz0`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handlePtpSourcingSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colMap = data?.colData?.map;
    const ssoId = this.ssoId;
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      jsonObj: JSON.stringify([{
        basket_name: colMap,
        pship_cc_key: rowData.PSHIP_CC_KEY,
        pship_acct_key: rowData.PSHIP_ACCT_KEY,
        ptr_le_key: rowData.PTR_LE_KEY,
        trading_partner: rowData.TRADING_PARTNER,
        basket_value: colMap ? rowData?.[colMap] : undefined,
        country_code: rowData.COUNTRY_CODE
      }]),
      jsonSettings: JSON.stringify([{
        tax_year: filterParams?.tax_year || globalContext?.globalParams?.tax_year
      }]),
      sso_id: ssoId,
      issue_key: rowData.ISSUE_KEY
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=jg2bxp`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleReviewAutoCheckboxSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.reviewAdjAutomationService.saveReviewAdj(
        this.GENERAL_CONFIG.base_url,
        [rowData],
        [rowData],
        globalContext?.globalParams || {},
        filterParams,
        rowData
      )
    );
  }

  private handleSaveCommentSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colMap = data?.colData?.map;
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      object_id: rowData.object_id,
      comment: colMap ? rowData?.[colMap] : rowData?.comment,
      ...(colMap ? { [colMap]: rowData?.[colMap] } : {}),
      tax_year: rowData.TAX_YEAR || filterParams?.tax_year || globalContext?.globalParams?.tax_year,
      division: rowData.DIVISION,
      leid: rowData.LEID,
      is_issue_key_enabled: 'N',
      scenarioCode: 'RAF'
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_id=33353`,
        payload,
        { withCredentials: true }
      ),
      'Comment has been saved',
      'Error occurred while saving comment'
    );
  }

  private handleSaveFdiiQbaiAcctExcludeSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.firFdiiQbaiExcludeAcctService.saveFdiiQbaiAcctExclude(
        this.GENERAL_CONFIG.base_url,
        data,
        globalContext?.globalParams || {},
        filterParams,
        data?.rowData || {}
      )
    );
  }

  private handleSaveFdiiQbaiAmtSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.firFdiiQbaiExcludeAcctService.saveFdiiQbaiAmt(
        this.GENERAL_CONFIG.base_url,
        data,
        globalContext?.globalParams || {},
        filterParams,
        data?.rowData || {}
      )
    );
  }

  private handleSaveFdiiQbaiPtrAmtSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.firFdiiQbaiExcludeAcctService.saveFdiiQbaiPtrAmt(
        this.GENERAL_CONFIG.base_url,
        data,
        globalContext?.globalParams || {},
        filterParams,
        data?.rowData || {}
      )
    );
  }

  private handleSaveGILTIHTEAnalysisOverrideSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colId = data?.col_id;
    if (!colId || rowData?.[colId] === undefined || rowData?.[colId] === null) {
      this.gtwSnackbarService.openSnackBar(
        'You cannot enter null or empty values, Please enter valid values',
        'gtw-snackbar--red'
      );
      return of({
        callSuccess: '0',
        errorMessage: 'You cannot enter null or empty values, Please enter valid values'
      });
    }

    const ssoId = this.ssoId;
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      jsonObj: JSON.stringify([{
        ho_combination_key: rowData.HO_COMBINATION_KEY,
        tested_unit: rowData.TESTED_UNIT_NAME,
        group_type: rowData.GROUP_TYPE,
        gilti_hte_map: colId,
        basket_id: rowData.BASKET_ID,
        value: rowData[colId]
      }]),
      jsonSettings: JSON.stringify([{
        tax_year: filterParams?.tax_year || globalContext?.globalParams?.tax_year || rowData?.TAX_YEAR,
        sso_id: ssoId,
        screen_key: globalContext?.globalParams?.screen_key
      }]),
      object_id: rowData.object_id,
      sso_id: ssoId,
      process_name: 'Save GILTI HTE analysis override'
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=fo97y6`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveTBBSDiagnosticsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      jsonObj: JSON.stringify(rowData),
      jsonSettings: JSON.stringify({
        tax_year: filterParams?.tax_year || globalContext?.globalParams?.tax_year || rowData?.TAX_YEAR,
        scenario: filterParams?.scenario || globalContext?.globalParams?.scenario || rowData?.SCENARIO,
        jcd_key: globalContext?.globalParams?.jcd_key === '' || globalContext?.globalParams?.jcd_key === undefined
          ? 250
          : globalContext?.globalParams?.jcd_key
      }),
      process_name: 'Save_TBBS_DIAGNOSTICS'
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=vl20dz`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveTestedUnitsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colId = data?.col_id;
    if (!colId || rowData?.[colId] === undefined || rowData?.[colId] === null) {
      this.gtwSnackbarService.openSnackBar(
        'You cannot enter null or empty values, Please enter valid values',
        'gtw-snackbar--red'
      );
      return of({
        callSuccess: '0',
        errorMessage: 'You cannot enter null or empty values, Please enter valid values'
      });
    }

    const ssoId = this.ssoId;
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      jsonObj: JSON.stringify([{
        combination_key: rowData.COMBINATION_KEY,
        ho_combination_key: rowData.HO_COMBINATION_KEY,
        col_type: colId,
        col_value: rowData[colId]
      }]),
      jsonSettings: JSON.stringify([{
        tax_year: filterParams?.tax_year || globalContext?.globalParams?.tax_year || rowData?.TAX_YEAR,
        sso_id: ssoId,
        screen_key: globalContext?.globalParams?.screen_key
      }]),
      object_id: rowData.object_id,
      sso_id: ssoId,
      process_name: 'Save Tested Units'
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=rbzvw7`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSave851FormDataSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const jsonObj = Array.isArray(data?.jsonObj)
      ? data.jsonObj
      : Array.isArray(data?.clob_data)
      ? data.clob_data
      : Object.keys(rowData).length > 0
      ? [rowData]
      : [];
    const jsonSettings = Array.isArray(data?.jsonSettings)
      ? data.jsonSettings
      : Array.isArray(data?.clob_settings)
      ? data.clob_settings
      : [{
          tax_year: filterParams?.tax_year || globalContext?.globalParams?.tax_year || rowData?.TAX_YEAR,
          scenario: filterParams?.scenario || globalContext?.globalParams?.scenario || rowData?.SCENARIO,
          jcd_key: filterParams?.jcd_key || globalContext?.globalParams?.jcd_key || rowData?.JCD_KEY || 250
        }];

    if (!jsonObj.length) {
      return of({
        callSuccess: '0',
        errorMessage: 'Unable to save 851 form data: normalized payload is empty.'
      });
    }

    return this.wrapSave(
      this.form851TaxualinfoService.saveForm851(
        this.GENERAL_CONFIG.base_url,
        jsonSettings,
        jsonObj,
        globalContext?.globalParams || {},
        filterParams,
        this.ssoId
      )
    );
  }

  private handleSave8990ScheduleBAmountsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colId = data?.colData?.map || data?.colData?.MAP || data?.col_id;
    const payload = {
      combination_key: String(rowData?.COMBINATION_KEY || ''),
      acct_key: String(rowData?.[colId + '_ACCT_KEY'] ?? ''),
      amount: String(rowData?.[colId] ?? '')
    };
    return this.wrapSave(
      this.fir8990ScheduleBService.save8990ScheduleBAmounts(
        this.GENERAL_CONFIG.base_url,
        payload,
        globalContext,
        filterParams,
        rowData
      )
    );
  }

  private handleSave901AdjustmentsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colData = data?.colData || {};
    const comments = rowData?.COMMENTS || rowData?.comment || '';
    return this.wrapSave(
      this.dashboard901MService.save901Adjustment(
        this.GENERAL_CONFIG.base_url,
        rowData,
        colData,
        comments,
        globalContext,
        filterParams
      )
    );
  }

  private handleSaveAmountDataSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const builtClobData = data?.col_id ? this.f4255PartIService.buildAmountDataClobData(data) : null;
    const clobData = Array.isArray(data?.clobData)
      ? data.clobData
      : Array.isArray(data?.clob_data)
      ? data.clob_data
      : Array.isArray(data?.jsonObj)
      ? data.jsonObj
      : builtClobData && builtClobData.length > 0
      ? builtClobData
      : Object.keys(rowData).length > 0
      ? [rowData]
      : [];
    const clobSettings = Array.isArray(data?.clobSettings)
      ? data.clobSettings
      : Array.isArray(data?.clob_settings)
      ? data.clob_settings
      : Array.isArray(data?.jsonSettings)
      ? data.jsonSettings
      : [{
          tax_year: filterParams?.tax_year || globalContext?.globalParams?.tax_year || rowData?.TAX_YEAR,
          scenario: filterParams?.scenario || globalContext?.globalParams?.scenario || rowData?.SCENARIO,
          jcd_key: filterParams?.jcd_key || globalContext?.globalParams?.jcd_key || rowData?.JCD_KEY || 250
        }];

    if (!clobData.length) {
      return of({
        callSuccess: '0',
        errorMessage: 'Unable to save amount data: normalized payload is empty.'
      });
    }

    return this.wrapSave(
      this.f4255PartIService.saveAmountData(
        this.GENERAL_CONFIG.base_url,
        clobData,
        clobSettings,
        globalContext,
        filterParams
      )
    );
  }

  private handleSaveCashPaymentFlagSave(data: any): Observable<any> {
    const rowData = data?.rowData || {};
    const oldRowData = data?.oldRowData || {};
    const colId = data?.col_id;
    if (colId && rowData?.[colId] === oldRowData?.[colId]) {
      return of({ callSuccess: '1', skipped: true });
    }

    const payload = this.buildRelatedPartyPaymentPayload(
      data,
      'saveCashPaymentFlag',
      String(rowData?.CASH_PAYMENT) === '1' ? 'Y' : 'N'
    );

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=vxe5m1`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveCreditFacilityDataSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const globalParams = globalContext?.globalParams || {};

    const savePayload = {
      trans_details_key: rowData.trans_details_key || rowData.TRANS_DETAILS_KEY || '',
      line_attrib_key: rowData.line_attrib_key || rowData.LINE_ATTRIB_KEY || '',
      occurrence: rowData.occurrence || rowData.OCCURRENCE || '',
      attrib_value: rowData.attrib_value || rowData.ATTRIB_VALUE || '',
      trans_type_key: rowData.trans_type_key || rowData.TRANS_TYPE_KEY || ''
    };

    const clobSettings = [{
      sso_id: this.ssoId,
      screen_key: globalParams.screen_key || filterParams?.screen_key
    }];

    const params: any = { ...globalParams, ...filterParams, ...savePayload };
    params.clob_data = JSON.stringify([savePayload]);
    params.clob_settings = JSON.stringify(clobSettings);
    params.process_name = 'SAVE_CREDIT_FACILITY';
    params.sso_id = this.ssoId;
    params.tax_year = params.tax_year || filterParams?.tax_year || '';
    params.scenario = params.scenario || filterParams?.scenario || '';
    params.jcd_key = params.jcd_key || filterParams?.jcd_key || '';
    delete params.data;
    delete params.row_id;

    const formData = new FormData();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        formData.append(key, params[key].toString());
      }
    });

    return this.http.post<any>(
      `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=cqkan4`,
      formData,
      { withCredentials: true }
    ).pipe(
      map((response: any) => {
        const errorMessage = response?.errorMessage;
        const callSuccess = response?.callSuccess;
        if (callSuccess === '1' || callSuccess === 1 || errorMessage === 'null' || !errorMessage) {
          this.gtwSnackbarService.openSnackBar('Data saved Successfully.', 'gtw-snackbar--green');
          return { ...response, callSuccess: '1' };
        }
        this.gtwSnackbarService.openSnackBar(errorMessage || 'Error occurred while saving', 'gtw-snackbar--red');
        throw new Error(errorMessage || 'Error occurred while saving');
      })
    );
  }

  private handleSaveCurrentSignoffSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      jsonObj: JSON.stringify([{
        combination_key: rowData.COMBINATION_KEY,
        chart_key: rowData.CHART_KEY,
        trans_header_key: rowData.SIGNOFF_TRANS_HEADER_KEY === undefined ? 0 : rowData.SIGNOFF_TRANS_HEADER_KEY,
        signoff_y_n: rowData.SIGNOFF_Y_N,
        issue_key: rowData.ISSUE_KEY
      }]),
      jsonSetting: JSON.stringify([{
        combination_key: rowData.COMBINATION_KEY,
        tax_year: rowData.TAX_YEAR
      }])
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=84f3zb`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveF1120LSchdADetailsSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      ...globalContext?.globalParams,
      process_name: 'SCH_A_EDIT_SAVE',
      clob_data: JSON.stringify([rowData]),
      clob_setting: JSON.stringify({ tax_year: rowData.TAX_YEAR })
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=j3nkw2`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveF1120LSchdLDetailsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const mainData = data?.mainData || data?.saveData || data?.payload || data?.jsonObj;
    const hasExpectedShape =
      Array.isArray(mainData) &&
      mainData[0]?.editrows?.[0]?.[0]?.trans_type_key !== undefined &&
      mainData[0]?.editrows?.[0]?.[0]?.line_attrib_key !== undefined;
    if (!hasExpectedShape) {
      return of({
        callSuccess: '0',
        errorMessage: 'Unable to save F1120L Schedule L details: payload missing expected edit row structure.'
      });
    }
    return this.wrapSave(
      this.f1120lSchLService.saveF1120LScheduleLDetails(
        this.GENERAL_CONFIG.base_url,
        mainData,
        globalContext,
        filterParams,
        rowData
      )
    );
  }

  private handleSaveF1120PCSchdCDetailsSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      ...globalContext?.globalParams,
      process_name: 'SCH_C_EDIT_SAVE',
      clob_data: JSON.stringify([rowData]),
      clob_setting: JSON.stringify({ tax_year: rowData.TAX_YEAR })
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=j3nkw2`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveF1120LPCHeaderSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const clobSettings = data?.returnClobSettingsObj || data?.clob_settings || data?.jsonSettings || {};
    const clobData = data?.fDomesticSaveDtls || data?.clob_data || data?.jsonObj || [];
    if (!clobSettings || (Array.isArray(clobData) && clobData.length === 0)) {
      return of({
        callSuccess: '0',
        errorMessage: 'Unable to save F1120L PC header: payload is incomplete.'
      });
    }
    return this.wrapSave(
      this.f1120LPCHeadersService.save1120LPCHeaderDetails(
        this.GENERAL_CONFIG.base_url,
        clobSettings,
        clobData,
        globalContext,
        filterParams,
        data?.rowData || {}
      )
    );
  }

  private handleSaveF1120SchdCDetailsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    if (rowData?.SCENARIO !== rowData?.SCENARIO_KEY) {
      this.gtwSnackbarService.openSnackBar(
        'Selected Scenario is Locked, please verify and update the amount.',
        'gtw-snackbar--red'
      );
      return of({
        callSuccess: '0',
        errorMessage: 'Selected Scenario is Locked, please verify and update the amount.'
      });
    }

    const globalParams = globalContext?.globalParams || {};
    const issueSettings = this.globalService?.isIssueIDEnabled
      ? this.globalService.isIssueIDEnabled(globalParams?.tax_year, globalParams?.scenario)
      : {};
    const payload = {
      ...globalParams,
      ...filterParams,
      scenarioCode: issueSettings?.scenarioCode,
      is_issue_key_enabled: issueSettings?.is_issue_key_enabled,
      process_name: 'SCH_C_EDIT_SAVE',
      tax_year: rowData.TAX_YEAR,
      scenario: rowData.SCENARIO,
      jcd_key: rowData.JCD_KEY,
      clob_data: JSON.stringify([rowData]),
      clob_setting: JSON.stringify({ tax_year: rowData.TAX_YEAR })
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=ljhm8x`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveF3800PIIIConsolAmountsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colId = data?.col_id || '';
    const colValue = Number(rowData?.[colId] || 0);
    const totalRemaining = Number(rowData?.HIDDEN_TOTAL_REMAINING || 0);
    if (colValue > totalRemaining) {
      this.gtwSnackbarService.openSnackBar(
        "Please make sure utilized Amount can't be greater that Total Remaining Amount",
        'gtw-snackbar--red'
      );
      return of({
        callSuccess: '0',
        errorMessage: "Please make sure utilized Amount can't be greater that Total Remaining Amount"
      });
    }

    const utilizedTaxYr = String(colId).includes('_') ? String(colId).split('_')[0] : '';
    const carryForwardAmts = Object.keys(rowData || {})
      .filter((key) => key.endsWith('CarryForward') && key.split('_')[0] < utilizedTaxYr)
      .reduce((acc: Record<string, number>, key) => {
        acc[key] = Number(rowData[key] || 0);
        return acc;
      }, {});
    const sumCarryForwardAmts = Object.values(carryForwardAmts).reduce((a: number, b: number) => a + b, 0);
    if (sumCarryForwardAmts !== 0) {
      this.gtwSnackbarService.openSnackBar(
        'Please make sure previous year CarryForward Amount should be exhaused ',
        'gtw-snackbar--red'
      );
      return of({
        callSuccess: '0',
        errorMessage: 'Please make sure previous year CarryForward Amount should be exhaused '
      });
    }

    const carrySourceKey = String(utilizedTaxYr) === String(rowData?.TAX_YEAR)
      ? `${rowData?.TAX_YEAR}_Generated`
      : `${utilizedTaxYr}_CarryOver`;
    const carrySourceValue = Number(rowData?.[carrySourceKey] || 0);
    if (colValue > carrySourceValue) {
      this.gtwSnackbarService.openSnackBar(
        "Please make sure utilized Amount can't be greater that Generated/CarryOver Amount",
        'gtw-snackbar--red'
      );
      return of({
        callSuccess: '0',
        errorMessage: "Please make sure utilized Amount can't be greater that Generated/CarryOver Amount"
      });
    }

    const clobData = [{
      acct_key: rowData?.ACCT_KEY,
      part_no: rowData?.PART_NO,
      schd_tax_year: utilizedTaxYr,
      u_amount: colValue < 0 ? colValue : -colValue
    }];
    const clobSetting = {
      tax_year: rowData.TAX_YEAR,
      scenario: rowData.SCENARIO,
      jcd_key: rowData.JCD,
      ta_key: rowData.TA_KEY,
      combination_key: rowData.COMBINATION_KEY
    };

    return this.wrapSave(
      this.f3800PIIIConsolService.saveCreditForms(
        this.GENERAL_CONFIG.base_url,
        clobSetting,
        clobData,
        globalContext,
        filterParams,
        rowData
      )
    );
  }

  private handleSaveInsSchPDetailsSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const clobSetting = {
      ...globalContext?.globalParams,
      tax_year: rowData.TAX_YEAR,
      grid_name: rowData.GRID_NAME
    };
    const payload = {
      ...globalContext?.globalParams,
      process_name: 'SCH_P_EDIT_SAVE',
      clob_data: JSON.stringify([rowData]),
      clob_setting: JSON.stringify(clobSetting)
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=nak559`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveNonFinancialSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colMap = data?.colData?.map;
    const firEinRaw = rowData?.FIR_EIN;

    if (colMap === 'FIR_EIN' && firEinRaw !== null && firEinRaw !== undefined && String(firEinRaw).length !== 9) {
      this.gtwSnackbarService.openSnackBar('FIR_EIN has to be 9 digits', 'gtw-snackbar--red');
      if (data?.oldRowData?.FIR_EIN !== undefined) {
        rowData.FIR_EIN = data.oldRowData.FIR_EIN;
      }
      return of({ callSuccess: '0', errorMessage: 'FIR_EIN has to be 9 digits' });
    }

    const preloadParams = {
      tax_year: filterParams?.tax_year || globalContext?.globalParams?.tax_year,
      scenario: filterParams?.scenario || globalContext?.globalParams?.scenario,
      jcd_key: globalContext?.globalParams?.jcd_key || 250,
      combination_key: rowData?.HO_COMBINATION_KEY,
      attrib_name: colMap,
      action: 'grid'
    };

    return this.form5471TextinfoService.getCatgFilerData(
      this.GENERAL_CONFIG.base_url,
      preloadParams,
      {},
      {}
    ).pipe(
      take(1),
      switchMap((preloadResponse: any) => {
        const preloadData = Array.isArray(preloadResponse?.jsonObject) ? preloadResponse.jsonObject : [];
        const currentValue = Number(rowData?.[colMap]);
        const baselineValue = preloadData[0]?.VALUE === undefined ? null : Number(preloadData[0].VALUE);
        const overrideFlag = baselineValue !== null && currentValue === baselineValue ? 'N' : 'Y';
        const normalizedEin = colMap === 'FIR_EIN' && String(firEinRaw || '').length === 9
          ? `${String(firEinRaw).slice(0, 2)}-${String(firEinRaw).slice(2)}`
          : firEinRaw;

        const jsonObj = [{
          fir_ein: colMap === 'COPY_FROM_GOLD' ? rowData?.GOLD_EIN : normalizedEin,
          is_disregarded_date: colMap === 'DISREGARDED_DATE_8858' ? 'Y' : 'N',
          disregarded_date_8858: rowData?.DISREGARDED_DATE_8858,
          is_direct_owner_delete: colMap === 'DELETE_DIRECT_OWNER' ? 'Y' : 'N',
          is_direct_owner_assign: colMap === 'ASSIGN_DIRECT_OWNER' ? 'Y' : 'N',
          direct_owner: data?.vm?.owner?.selectedleid?.PARENT_LE_ID || rowData?.DIRECT_OWNER_8858,
          category_1: rowData?.CATEGORY_1 === 1 ? 1 : 0,
          category_2: rowData?.CATEGORY_2 === 1 ? 1 : 0,
          category_3: rowData?.CATEGORY_3 === 1 ? 1 : 0,
          category_4: rowData?.CATEGORY_4 === 1 ? 1 : 0,
          category_5: rowData?.CATEGORY_5 === 1 ? 1 : 0,
          category_1a: rowData?.CATEGORY_1A === 1 ? 1 : 0,
          category_1b: rowData?.CATEGORY_1B === 1 ? 1 : 0,
          category_1c: rowData?.CATEGORY_1C === 1 ? 1 : 0,
          category_5a: rowData?.CATEGORY_5A === 1 ? 1 : 0,
          category_5b: rowData?.CATEGORY_5B === 1 ? 1 : 0,
          category_5c: rowData?.CATEGORY_5C === 1 ? 1 : 0,
          is_ein_update: colMap === 'FIR_EIN' || colMap === 'COPY_FROM_GOLD' ? 'Y' : 'N',
          is_category_update_1: colMap === 'CATEGORY_1' ? 'Y' : 'N',
          is_category_update_2: colMap === 'CATEGORY_2' ? 'Y' : 'N',
          is_category_update_3: colMap === 'CATEGORY_3' ? 'Y' : 'N',
          is_category_update_4: colMap === 'CATEGORY_4' ? 'Y' : 'N',
          is_category_update_5: colMap === 'CATEGORY_5' ? 'Y' : 'N',
          is_category_update_1a: colMap === 'CATEGORY_1A' ? 'Y' : 'N',
          is_category_update_1b: colMap === 'CATEGORY_1B' ? 'Y' : 'N',
          is_category_update_1c: colMap === 'CATEGORY_1C' ? 'Y' : 'N',
          is_category_update_5a: colMap === 'CATEGORY_5A' ? 'Y' : 'N',
          is_category_update_5b: colMap === 'CATEGORY_5B' ? 'Y' : 'N',
          is_category_update_5c: colMap === 'CATEGORY_5C' ? 'Y' : 'N',
          is_category_override_1: colMap === 'CATEGORY_1' && overrideFlag === 'Y' ? 'Y' : 'N',
          is_category_override_3: colMap === 'CATEGORY_3' && overrideFlag === 'Y' ? 'Y' : 'N',
          is_category_override_4: colMap === 'CATEGORY_4' && overrideFlag === 'Y' ? 'Y' : 'N',
          is_category_override_5: colMap === 'CATEGORY_5' && overrideFlag === 'Y' ? 'Y' : 'N',
          is_category_override_1a: colMap === 'CATEGORY_1A' && overrideFlag === 'Y' ? 'Y' : 'N',
          is_category_override_5a: colMap === 'CATEGORY_5A' && overrideFlag === 'Y' ? 'Y' : 'N'
        }];

        const jsonSettings = [{
          tax_year: filterParams?.tax_year || globalContext?.globalParams?.tax_year || rowData?.TAX_YEAR,
          combination_key: rowData?.COMBINATION_KEY,
          tax_type: rowData?.TAX_TYPE
        }];

        const savePayload = {
          ...globalContext?.globalParams,
          ...filterParams,
          tax_year: filterParams?.tax_year
            || globalContext?.globalParams?.tax_year
            || rowData?.TAX_YEAR,
          scenario: filterParams?.scenario
            || globalContext?.globalParams?.scenario,
          jcd_key: filterParams?.jcd_key
            || globalContext?.globalParams?.jcd_key
            || 250,
          scenarioCode: globalContext?.globalParams?.scenarioCode || 'RAF',
          is_issue_key_enabled: globalContext?.globalParams?.is_issue_key_enabled || 'N',
          process_name: 'Non_Financial_Save',
          jsonSettings: JSON.stringify(jsonSettings),
          jsonObj: JSON.stringify(jsonObj)
        };

        return this.http.post<any>(
          `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=zqcwb8`,
          savePayload,
          { withCredentials: true }
        );
      }),
      map((response: any) => {
        if (response?.callSuccess === '1' || response?.callSuccess === 1) {
          this.gtwSnackbarService.openSnackBar('Data saved Successfully.', 'gtw-snackbar--green');
          return response;
        }
        const msg = response?.errorMessage && response.errorMessage !== 'null'
          ? response.errorMessage : 'Error occurred while saving';
        this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--red');
        throw new Error(msg);
      })
    );
  }

  private handleSaveSanctionedCountriesSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const globalParams = globalContext?.globalParams || {};
    const jsonSettings = [{
      combination_key: rowData.COMBINATION_KEY,
      tax_year: globalParams.tax_year,
      scenario: globalParams.scenario,
      jcd_key: globalParams.jcd_key === '' || globalParams.jcd_key === undefined ? 250 : globalParams.jcd_key
    }];
    const jsonObj = [{
      sanctioned_country: rowData.SANCTIONED_COUNTRY,
      illegal_payments: rowData.ILLEGAL_PAYMENTS,
      ISSUE_KEY: globalParams.issue_id === undefined ? null : parseInt(String(globalParams.issue_id), 10)
    }];

    return this.wrapSave(
      this.firSanctionedCountriesService.saveSanctionedCountries(
        this.GENERAL_CONFIG.base_url,
        jsonObj,
        jsonSettings,
        globalContext,
        filterParams
      )
    );
  }

  private handleSaveTaxTrueUpSignoffSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.firTrueupTaxesService.saveTaxTrueUpSignoff(
        this.GENERAL_CONFIG.base_url,
        data?.rowData || {},
        data?.colData || {},
        globalContext,
        filterParams
      )
    );
  }

  private handleSaveAdjustmentsDetailsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colData = data?.colData || {};
    const clobData = [{
      ...rowData,
      edited_column: colData?.map,
      edited_value: colData?.map ? rowData?.[colData.map] : undefined
    }];
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      process_name: 'State_Elimination_Adjustment_Details',
      clob_data: JSON.stringify(clobData),
      clob_setting: JSON.stringify({ tax_year: rowData.TAX_YEAR }),
      scenario: filterParams?.scenario
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=mvttbv`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveAmtPaidSave(data: any): Observable<any> {
    const rowData = data?.rowData || {};
    const colId = data?.col_id;
    const value = colId ? rowData?.[colId] : rowData?.AMT_PAID;
    if (value === '' || value === undefined || value === null) {
      this.gtwSnackbarService.openSnackBar('Please enter valid Amount Paid', 'gtw-snackbar--red');
      return of({ callSuccess: '0', errorMessage: 'Please enter valid Amount Paid' });
    }
    if (Number(value) < 0) {
      this.gtwSnackbarService.openSnackBar('Amt Paid In HO has to be greater than zero', 'gtw-snackbar--red');
      return of({ callSuccess: '0', errorMessage: 'Amt Paid In HO has to be greater than zero' });
    }

    const payload = this.buildRelatedPartyPaymentPayload(data, 'saveAmtPaid', String(rowData?.AMT_PAID ?? ''));
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=vxe5m1`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveAmtReceivedInHoSave(data: any): Observable<any> {
    const rowData = data?.rowData || {};
    const colId = data?.col_id;
    const value = colId ? rowData?.[colId] : rowData?.AMT_RECEIVED;
    if (value === '' || value === undefined || value === null) {
      this.gtwSnackbarService.openSnackBar('Please enter valid Amount Received', 'gtw-snackbar--red');
      return of({ callSuccess: '0', errorMessage: 'Please enter valid Amount Received' });
    }
    if (Number(value) > 0) {
      this.gtwSnackbarService.openSnackBar('Amt Received In HO has to be less than zero', 'gtw-snackbar--red');
      return of({ callSuccess: '0', errorMessage: 'Amt Received In HO has to be less than zero' });
    }

    const payload = this.buildRelatedPartyPaymentPayload(data, 'saveAmtReceivedInHO', String(rowData?.AMT_RECEIVED ?? ''));
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=vxe5m1`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveCbcrVarianceTypeSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=h4bo44`,
        {
          sso_id: this.ssoId,
          tax_year: filterParams?.tax_year,
          scenario: filterParams?.scenario,
          user_variance: rowData.USER_MODIFIED,
          variance_key: rowData.VARIANCE_KEY,
          category: rowData.CATEGORY
        },
        { withCredentials: true }
      )
    );
  }

  private handleSaveCommentHeaderSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colData = data?.colData || {};
    const colMap = colData.map;
    const rawComment = colMap ? rowData[colMap] : undefined;
    const comments = String(rawComment ?? rowData.COMMENTS ?? rowData.DETAIL_COMMENTS ?? rowData.comment ?? '');

    // Non-FIR screens (e.g. G/L Mappings, screen 155) reuse this callback but their
    // rowData lacks FIR keys. GROUP_OBJ_KEY is an accepted COMBINATION_KEY FK for
    // those screens; LOCAL_CHART_KEY maps to CHART_KEY. Empirically verified against
    // PKG_GT_ANALYZE_CURRENT_TAXES.PROC_SAVE_COMMENTS on 2026-04-23.
    const firRowData = rowData.COMBINATION_KEY != null
      ? rowData
      : {
          ...rowData,
          COMBINATION_KEY: rowData.GROUP_OBJ_KEY ?? 0,
          CHART_KEY: rowData.CHART_KEY ?? rowData.LOCAL_CHART_KEY ?? 0,
          ISSUE_KEY: rowData.ISSUE_KEY ?? 0,
        };

    return this.wrapSave(
      this.firAnalyzeCurrentTaxesService.saveComment(
        this.GENERAL_CONFIG.base_url,
        firRowData,
        comments,
        globalContext?.globalParams || {},
        filterParams
      )
    );
  }

  private handleSaveDclDetailsSave(data: any): Observable<any> {
    return this.handleDclSave(data, 'exhb8l', 'DCL_DATA_SAVE_PHASE2');
  }

  private handleSaveDclTransDetailsSave(data: any): Observable<any> {
    return this.handleDclSave(data, 'yacpis', 'DCL_EDIT_SAVE');
  }

  private handleSaveEliminationAmountSave(data: any): Observable<any> {
    const rows = Array.isArray(data)
      ? data
      : Array.isArray(data?.gridData)
        ? data.gridData
        : Array.isArray(data?.rows)
          ? data.rows
          : [];
    return this.wrapSave(
      this.fir8858GaapDrilldownService.ELIMINATION_SAVE(rows),
      'Data saved Successfully.',
      '*Elimination must net to 0.'
    );
  }

  private handleSaveGhostScenarioAdjustmentsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const oldRowData = data?.oldRowData || {};
    const colId = data?.col_id;

    if (!colId) {
      return of({ callSuccess: '0', errorMessage: 'Column id is required for ghost scenario adjustments save.' });
    }

    if (rowData?.GGROWCOLORTOTAL === 1) {
      rowData[colId] = oldRowData?.[colId];
      return of({ callSuccess: '0', errorMessage: 'This cell is not editable.' });
    }

    const newValue = rowData?.[colId];
    if (newValue === null || newValue === undefined || String(newValue).trim() === '') {
      rowData[colId] = oldRowData?.[colId];
      return of({ callSuccess: '0', errorMessage: 'Please enter a valid amount.' });
    }

    if (newValue === oldRowData?.[colId]) {
      return of({ callSuccess: '1', skipped: true });
    }

    const ssoId = this.ssoId;
    const jsonSettings = {
      ...filterParams,
      jcd_key: globalContext?.globalParams?.jcd_key || filterParams?.jcd_key || 250
    };
    const jsonObj = {
      ...rowData,
      SCENARIO_ADJUSTMENTS: newValue,
      FILING_GROUP: rowData?.FILING_GROUP
    };

    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      sso_id: ssoId,
      process_name: 'SAVE_GHOST_SCENARIO_ADJUSTMENTS',
      jsonSettings: JSON.stringify(jsonSettings),
      jsonObj: JSON.stringify(jsonObj)
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=hp1utk`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveGhostSchdmExcludeSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const globalServiceForTemplate = {
      globalParams: globalContext?.globalParams || {},
      userSettingsData: this.globalService?.global?.inputs?.userSettingsData || {}
    };
    return this.wrapSave(
      this.addSchdmTemplateIdService.saveTemplate(
        this.GENERAL_CONFIG.base_url,
        rowData.SCHDM_TEMPLATE_ID,
        rowData.IS_EXCLUDED === 1 || rowData.IS_EXCLUDED === 'Y' ? 'Y' : 'N',
        globalServiceForTemplate,
        filterParams
      )
    );
  }

  private handleSaveGhostSourcingBasketDataSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colId = data?.col_id || data?.colData?.map || '';
    const settings = { ...filterParams, col_id: colId };
    return this.wrapSave(
      this.sourcingInputAppBasketService.saveGhostSourcingBasketData(
        this.GENERAL_CONFIG.base_url,
        rowData as any,
        settings,
        globalContext?.globalParams || {},
        filterParams,
        rowData
      )
    );
  }

  private handleSaveGiltiCalcSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colId = data?.col_id || data?.colData?.map || data?.colData?.MAP || '';
    return this.wrapSave(
      this.firGiltiCalcGridService.saveManualData(
        this.GENERAL_CONFIG.base_url,
        rowData,
        colId,
        globalContext,
        filterParams
      )
    );
  }

  private handleSaveGiltiConsolidatedAmtsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colId = data?.col_id || data?.colData?.map || data?.colData?.MAP || '';
    return this.wrapSave(
      this.firGiltiConsolidatedGridService.saveManualData(
        this.GENERAL_CONFIG.base_url,
        rowData,
        colId,
        globalContext,
        filterParams
      )
    );
  }

  private handleSaveIllegalPaymentsSave(data: any): Observable<any> {
    return this.handleSaveSanctionedCountriesSave(data);
  }

  private handleSaveIncludeFlagSave(data: any): Observable<any> {
    const rowData = data?.rowData || {};
    const oldRowData = data?.oldRowData || {};
    const colId = data?.col_id;
    if (colId && rowData?.[colId] === oldRowData?.[colId]) {
      return of({ callSuccess: '1', skipped: true });
    }

    const payload = this.buildRelatedPartyPaymentPayload(
      data,
      'saveIncludeFlag',
      String(rowData?.INCLUDE) === '1' ? 'Y' : 'N'
    );
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=vxe5m1`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveLocalAcctDescSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const details = data?.Details || data?.details || data?.jsonObj || rowData;
    const settings = data?.settings || data?.jsonSettings || rowData;
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      jsonObj: typeof details === 'string' ? details : JSON.stringify(details),
      jsonSettings: typeof settings === 'string' ? settings : JSON.stringify(settings),
      process_name: 'LOCAL_TO_SYSTEM_SAVE'
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=hhba21`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveLocalPriorYearAmtSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const oldRowData = data?.oldRowData || {};

    if (rowData.CHART_KEY !== rowData.CY_CHART_KEY) {
      rowData.ADJ_FOR_OPEN_BS = oldRowData.ADJ_FOR_OPEN_BS;
      this.gtwSnackbarService.openSnackBar('You cannot make changes to prior year balance.', 'gtw-snackbar--red');
      return of({
        callSuccess: '0',
        errorMessage: 'You cannot make changes to prior year balance.'
      });
    }

    const adjForOpenBs = Number(rowData.ADJ_FOR_OPEN_BS || 0);
    const priorYearSubmission = Number(rowData.PRIOR_YEAR_SUBMISSION || 0);
    rowData.ADJ_BALANCE = priorYearSubmission + adjForOpenBs;

    const issueId = globalContext?.globalParams?.issue_id;
    const jsonSettings = [{
      tax_year: filterParams?.tax_year || globalContext?.globalParams?.tax_year || rowData.TAX_YEAR,
      issue_key: issueId === undefined || issueId === null || issueId === '' ? null : parseInt(String(issueId), 10)
    }];

    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      jsonObj: JSON.stringify(rowData),
      jsonSettings: JSON.stringify(jsonSettings),
      process_name: 'saveLocalAdjPriorYearData'
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_id=107`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveMaxLoanUserAmtSave(data: any): Observable<any> {
    const rowData = data?.rowData || {};
    const oldRowData = data?.oldRowData || {};
    if (rowData.USER_AMT === null || rowData.USER_AMT === '' || rowData.USER_AMT === undefined) {
      rowData.USER_AMT = oldRowData.USER_AMT === undefined ? 0 : oldRowData.USER_AMT;
      this.gtwSnackbarService.openSnackBar('Cannot enter null value in Override Column', 'gtw-snackbar--red');
      return of({
        callSuccess: '0',
        errorMessage: 'Cannot enter null value in Override Column'
      });
    }

    const payload = {
      jsonObj: JSON.stringify(rowData),
      jsonSettings: '{}',
      process_name: 'saveMaxLoanDetails'
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=qx3meb`,
        payload,
        { withCredentials: true }
      ),
      'Data has been saved successfully',
      'Error occurred while saving max loan override'
    ).pipe(
      tap((response) => {
        if (!response?.skipped) {
          this.refreshActiveScreenGrid();
        }
      })
    );
  }

  private handleSaveQreCommentsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const globalParams = globalContext?.globalParams || {};
    const ssoId = this.ssoId;
    const payload = {
      ...globalParams,
      ...filterParams,
      jsonSettings: JSON.stringify([{
        sso_id: ssoId,
        tax_year: globalParams.tax_year,
        scenario: globalParams.scenario,
        jcd_key: globalParams.jcd_key === '' || globalParams.jcd_key === undefined ? 250 : globalParams.jcd_key,
        me_key: rowData.CY_ME_KEY
      }]),
      jsonObj: JSON.stringify([{
        sso_id: ssoId,
        me_key: rowData.CY_ME_KEY,
        comments: rowData.EXPLANATION
      }])
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=ogj5w1`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveQreCreditAdditionalDtlsSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = { ...(data?.rowData || {}) };
    const colId = data?.col_id;
    if (colId && rowData[colId] !== undefined) {
      rowData.LINE_NO = colId;
      rowData.ATTRIB_VALUE = rowData[colId];
    }
    const payload = {
      tax_year: globalContext?.globalParams?.tax_year,
      scenario: globalContext?.globalParams?.scenario,
      jcd_key: globalContext?.globalParams?.jcd_key,
      clob_settings: JSON.stringify(rowData),
      clob_data: JSON.stringify(rowData)
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=56g7av`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveQreCreditAllocationSave(data: any): Observable<any> {
    const { globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      tax_year: globalContext?.globalParams?.tax_year,
      scenario: globalContext?.globalParams?.scenario,
      jcd_key: globalContext?.globalParams?.jcd_key,
      jsonSettings: JSON.stringify(rowData),
      jsonObj: JSON.stringify(rowData)
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=rn8ybp`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveQreSignOffSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const globalParams = globalContext?.globalParams || {};
    const ssoId = this.ssoId;
    const payload = {
      ...globalParams,
      ...filterParams,
      jsonSettings: JSON.stringify([{
        sso_id: ssoId,
        tax_year: globalParams.tax_year,
        scenario: globalParams.scenario,
        jcd_key: globalParams.jcd_key === '' || globalParams.jcd_key === undefined ? 250 : globalParams.jcd_key,
        me_key: rowData.CY_ME_KEY
      }]),
      jsonObj: JSON.stringify([{
        sso_id: ssoId,
        me_key: rowData.CY_ME_KEY,
        sign_off: rowData.SIGNOFF === 0 ? 'N' : 'Y'
      }])
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=yqrl4y`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveQreYoySignoffDtlsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const globalParams = globalContext?.globalParams || {};
    const ssoId = this.ssoId;
    const payload = {
      ...globalParams,
      ...filterParams,
      clob_settings: JSON.stringify([{
        sso_id: ssoId,
        tax_year: globalParams.tax_year,
        scenario: globalParams.scenario,
        jcd_key: globalParams.jcd_key === '' || globalParams.jcd_key === undefined ? 250 : globalParams.jcd_key,
        me_key: rowData.CY_ME_KEY
      }]),
      clob_data: JSON.stringify([{
        sso_id: ssoId,
        me_key: rowData.CY_ME_KEY,
        NO_OF_BC: rowData.NO_OF_BC
      }])
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=yol7c7`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveSchJConsolAmountsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.f1120SchJService.saveScheduleJData(
        this.GENERAL_CONFIG.base_url,
        [rowData],
        globalContext,
        filterParams
      )
    );
  }

  private handleSaveSchdIGridDataSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const oldRowData = data?.oldRowData || {};
    const taxYear = Number(filterParams?.tax_year || globalContext?.globalParams?.tax_year || rowData?.TAX_YEAR || 0);
    const scenario = filterParams?.scenario || globalContext?.globalParams?.scenario;
    const jcdKey = globalContext?.globalParams?.jcd_key || filterParams?.jcd_key || 250;

    if (Number(rowData?.LINE_NO) === 6) {
      rowData.FIR_OVERRIDE_AMT = oldRowData.FIR_OVERRIDE_AMT;
      this.gtwSnackbarService.openSnackBar(
        'You cannot edit Line No 6 for total of Line 1 through 5.',
        'gtw-snackbar--red'
      );
      return of({
        callSuccess: '0',
        errorMessage: 'You cannot edit Line No 6 for total of Line 1 through 5.'
      });
    }

    const baseEditRow = {
      tax_year: taxYear,
      trans_type_key: rowData.TRANS_TYPE_KEY,
      combination_key: rowData.HO_COMBINATION_KEY,
      group_obj_key: rowData.GROUP_OBJ_KEY,
      form_key: rowData.FORM_KEY,
      line_no: rowData.LINE_NO,
      occurence: String(rowData.OCCURRENCE) === '0' ? null : rowData.OCCURRENCE,
      line_attrib_key: rowData.LINE_ATTRIB_KEY,
      Attribute_Name: 'FIR_OVERRIDE_AMT',
      Attribute_Value: rowData.FIR_OVERRIDE_AMT,
      trans_dtls_key: String(rowData.TRANS_DETAILS_KEY) === '0' ? null : rowData.TRANS_DETAILS_KEY,
      trans_status: 'A'
    };

    const saveSchedule = (editRows: any[], overridePayload: any[] = []): Observable<any> => {
      return new Observable((subscriber) => {
        this.fir5471ScheduleIService.saveScheduleI(
          this.GENERAL_CONFIG.base_url,
          [{ tax_year: taxYear, scenario, jcd_key: jcdKey }],
          { addrows: [], editrows: editRows, removerows: [] },
          globalContext?.globalParams || {},
          filterParams
        ).subscribe({
          next: (saveResponse: any) => {
            if (overridePayload.length > 0) {
              this.fir5471ScheduleIService.updateFlag(
                this.GENERAL_CONFIG.base_url,
                [{ combination_key: rowData.HO_COMBINATION_KEY, tax_year: taxYear }],
                overridePayload,
                globalContext?.globalParams || {},
                filterParams
              ).subscribe({
                next: () => {
                  subscriber.next(saveResponse);
                  subscriber.complete();
                },
                error: (error: any) => subscriber.error(error)
              });
              return;
            }
            subscriber.next(saveResponse);
            subscriber.complete();
          },
          error: (error: any) => subscriber.error(error)
        });
      }).pipe(
        take(1),
        map((response: any) => {
          if (response?.callSuccess === '1' || response?.callSuccess === 1) return response;
          throw response;
        })
      );
    };

    if (taxYear < 2018 && Number(rowData?.LINE_NO) < 6) {
      const loadPayload = { tax_year: taxYear, combination_key: rowData.HO_COMBINATION_KEY };
      return new Observable((subscriber) => {
        this.http.post<any>(
          `${this.GENERAL_CONFIG.base_url}/loadJsonObject?action_code=kij1vt`,
          loadPayload,
          { withCredentials: true }
        ).subscribe({
          next: (loadResponse: any) => {
            const rows = Array.isArray(loadResponse?.jsonObject) ? loadResponse.jsonObject : [];
            const line6 = rows.find((x: any) => Number(x?.LINE_NO) === 6);
            let line6Total = 0;
            rows.forEach((x: any) => {
              if (Number(x?.LINE_NO) >= 1 && Number(x?.LINE_NO) <= 5 && Number(x?.LINE_NO) !== Number(rowData?.LINE_NO)) {
                if (Number(x?.TRANS_DETAILS_KEY) !== 0) line6Total += Number(x?.ATTRIB_VALUE || 0);
                else if (Number(x?.GHOST_TRANS_DETAILS_KEY) !== 0) line6Total += Number(x?.GHOST_AMT_VALUE || 0);
              }
            });
            line6Total += Number(rowData?.FIR_OVERRIDE_AMT || 0);

            const line6Update = line6 ? {
              tax_year: taxYear,
              trans_type_key: rowData.TRANS_TYPE_KEY,
              combination_key: rowData.HO_COMBINATION_KEY,
              group_obj_key: rowData.GROUP_OBJ_KEY,
              form_key: rowData.FORM_KEY,
              line_no: line6.LINE_NO,
              occurence: String(line6.OCCURRENCE) === '0' ? null : line6.OCCURRENCE,
              line_attrib_key: line6.FOA_LINE_ATTRIB_KEY,
              Attribute_Name: 'FIR_OVERRIDE_AMT',
              Attribute_Value: line6Total,
              trans_dtls_key: String(line6.TRANS_DETAILS_KEY) === '0' ? null : line6.TRANS_DETAILS_KEY,
              trans_status: 'A'
            } : null;

            const overrideUpdates: any[] = [];
            const shouldUpdateOverride =
              ((rowData.FIR_OVERRIDE_AMT !== rowData.GHOST_AMT && rowData.GHOST_OVERRIDE_Y_N !== 'Y') ||
                (rowData.FIR_OVERRIDE_AMT === rowData.GHOST_AMT && rowData.GHOST_OVERRIDE_Y_N !== 'N')) &&
              Number(rowData.GHOST_TRANS_DETAILS_KEY) !== 0;
            if (shouldUpdateOverride) {
              overrideUpdates.push([{
                trans_header_key: Number(rowData.GHOST_TRANS_HEADER_KEY),
                trans_details_key: rowData.GHOST_TRANS_DETAILS_KEY,
                trans_type_key: rowData.GHOST_TRANS_TYPE_KEY,
                line_attrib_key: rowData.GHOST_LINE_ATTRIB_KEY,
                form_key: rowData.GHOST_FORM_KEY,
                OVERRIDE_Y_N: rowData.GHOST_OVERRIDE_Y_N === 'Y' ? 'N' : 'Y'
              }]);
            }

            const editRows = line6Update ? [[line6Update], [baseEditRow]] : [[baseEditRow]];
            saveSchedule(editRows, overrideUpdates).subscribe({
              next: (response) => {
                subscriber.next(response);
                subscriber.complete();
              },
              error: (error) => subscriber.error(error)
            });
          },
          error: (error: any) => subscriber.error(error)
        });
      }).pipe(take(1));
    }

    return saveSchedule([[baseEditRow]]);
  }

  private handleSaveSchdOSave(data: any): Observable<any> {
    return this.handleScheduleToggleSave(data, {
      actionCode: 'qyicdf',
      processName: 'Schedule_O_Save',
      transTypeKey: 'SCHEDULE_O_8865_ENABLED_TRANS_TYPE_KEY',
      lineNo: 'SCHEDULE_O_8865_ENABLED_LINE_NO',
      occurrence: 'SCHEDULE_O_8865_ENABLED_OCCURRENCE',
      lineAttribKey: 'SCHEDULE_O_8865_ENABLED_LINE_ATTRIB_KEY',
      attributeName: 'SCHEDULE_O_8865_ENABLED_ATTRIB_NAME',
      enabledFlag: 'SCHD_O_ENABLED',
      transDetailsKey: 'SCHEDULE_O_8865_ENABLED_TRANS_DETAILS_KEY'
    });
  }

  private handleSaveSchdOEnabledSave(data: any): Observable<any> {
    return this.handleScheduleToggleSave(data, {
      actionCode: '6qr0av',
      processName: 'Schedule_O_Save',
      transTypeKey: 'SCHEDULE_O_5471_ENABLED_TRANS_TYPE_KEY',
      lineNo: 'SCHEDULE_O_5471_ENABLED_LINE_NO',
      occurrence: 'SCHEDULE_O_5471_ENABLED_OCCURRENCE',
      lineAttribKey: 'SCHEDULE_O_5471_ENABLED_LINE_ATTRIB_KEY',
      attributeName: 'SCHEDULE_O_5471_ENABLED_ATTRIB_NAME',
      enabledFlag: 'SCHD_O_ENABLED',
      transDetailsKey: 'SCHEDULE_O_5471_ENABLED_TRANS_DETAILS_KEY'
    });
  }

  private handleSaveSchdPEnabledSave(data: any): Observable<any> {
    return this.handleScheduleToggleSave(data, {
      actionCode: 'ze6q6h',
      processName: 'Schedule_P_Save',
      transTypeKey: 'SCHEDULE_P_8865_ENABLED_TRANS_TYPE_KEY',
      lineNo: 'SCHEDULE_P_8865_ENABLED_LINE_NO',
      occurrence: 'SCHEDULE_P_8865_ENABLED_OCCURRENCE',
      lineAttribKey: 'SCHEDULE_P_8865_ENABLED_LINE_ATTRIB_KEY',
      attributeName: 'SCHEDULE_P_8865_ENABLED_ATTRIB_NAME',
      enabledFlag: 'SCHD_P_ENABLED',
      transDetailsKey: 'SCHEDULE_P_8865_ENABLED_TRANS_DETAILS_KEY'
    });
  }

  private handleSaveSection174CommentsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const globalParams = globalContext?.globalParams || {};
    const ssoId = this.ssoId;
    const payload = {
      ...globalParams,
      ...filterParams,
      jsonObj: JSON.stringify(rowData),
      jsonSettings: JSON.stringify([{
        sso_id: ssoId,
        tax_year: globalParams?.tax_year ?? rowData?.TAX_YEAR,
        scenario: globalParams?.scenario ?? rowData?.SCENARIO,
        jcd_key: globalParams?.jcd_key === '' || globalParams?.jcd_key === undefined
          ? 250
          : globalParams?.jcd_key,
        me_key: rowData?.CY_ME_KEY
      }])
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=p0l6qy`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveStmtFlagCheckedSave(data: any): Observable<any> {
    return this.handleScheduleToggleSave(data, {
      actionCode: '6qr0av',
      processName: 'Schedule_O_Save',
      transTypeKey: 'STMT_FLAG_TRANS_TYPE_KEY',
      lineNo: 'STMT_FLAG_LINE_NO',
      occurrence: 'STMT_FLAG_OCCURRENCE',
      lineAttribKey: 'STMT_FLAG_LINE_ATTRIB_KEY',
      attributeName: 'STMT_FLAG_ATTRIB_NAME',
      enabledFlag: 'STMT_FLAG_CHECKED',
      transDetailsKey: 'STMT_FLAG_TRANS_DETAILS_KEY'
    });
  }

  private handleSaveSuupplementDetailsSave(data: any): Observable<any> {
    return of({
      callSuccess: '1',
      skipped: true,
      message: 'Supplement save is handled through supplemental-info custom element events.'
    });
  }

  private handleSaveTableMaintananceSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.tblMaintenanceService.saveTableMaintenance(
        this.GENERAL_CONFIG.base_url,
        [rowData],
        [rowData],
        globalContext,
        filterParams
      )
    );
  }

  private handleSaveTaxAmtFlagSave(data: any): Observable<any> {
    const rowData = data?.rowData || {};
    const colId = data?.col_id;
    const value = colId ? rowData?.[colId] : rowData?.TAX_AMT;
    if (value === '' || value === undefined || value === null) {
      this.gtwSnackbarService.openSnackBar('Please enter valid  W/H Tax Amount', 'gtw-snackbar--red');
      return of({ callSuccess: '0', errorMessage: 'Please enter valid  W/H Tax Amount' });
    }
    if (Number(value) < 0) {
      this.gtwSnackbarService.openSnackBar('Error!!! W/H Tax Amount has to be greater than or equal to zero', 'gtw-snackbar--red');
      return of({
        callSuccess: '0',
        errorMessage: 'Error!!! W/H Tax Amount has to be greater than or equal to zero'
      });
    }

    const payload = this.buildRelatedPartyPaymentPayload(data, 'saveTaxAmtFlag', String(rowData?.TAX_AMT ?? ''));
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=vxe5m1`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSaveWwniAssetDataSave(data: any): Observable<any> {
    return this.handleWwniSave(data, 'ld99kg', 'WWNI_ASSET_LIABILITY_SAVE');
  }

  private handleSaveWwniCommDataSave(data: any): Observable<any> {
    return this.handleWwniSave(data, 'd1dvsu', 'WWNI_COMMENT_SAVE');
  }

  private handleSchdmselectorSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const checkedFlag = rowData?.SCHDM_SELECTOR === 1 || rowData?.SCHDM_SELECTOR === '1';
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      tax_year: rowData?.TAX_YEAR || filterParams?.tax_year || globalContext?.globalParams?.tax_year,
      scenario: globalContext?.globalParams?.scenario || filterParams?.scenario,
      jcd_key: globalContext?.globalParams?.jcd_key || filterParams?.jcd_key || 250,
      combination_key: rowData?.COMBINATION_KEY,
      checkedFlag,
      jsonObj: JSON.stringify(rowData)
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=8c5xg1`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleSettlementBusSignoffSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      leid: rowData.LEID,
      division: rowData.DIVISION,
      type: 'BUSINESS',
      on_off: rowData.Bus_Sign_Off_0 === 1 ? '1' : '0',
      user_name: this.globalService?.global?.inputs?.userSettingsData?.user?.display_name
        || this.ssoId
        || '',
      is_issue_key_enabled: 'N',
      scenarioCode: 'RAF'
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_id=33351`,
        payload,
        { withCredentials: true }
      ),
      'Success',
      'Error occurred while saving settlement business signoff'
    );
  }

  private handleSettlementCorpSignoffSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      leid: rowData.LEID,
      division: rowData.DIVISION,
      type: 'CORPORATE',
      on_off: rowData.Settled_SignOff_0 === 1 ? '1' : '0',
      user_name: this.globalService?.global?.inputs?.userSettingsData?.user?.display_name
        || this.ssoId
        || '',
      is_issue_key_enabled: 'N',
      scenarioCode: 'RAF'
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_id=33351`,
        payload,
        { withCredentials: true }
      ),
      'Success',
      'Error occurred while saving settlement corporate signoff'
    );
  }

  private handleSpecialAllocOverrideSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const ssoId = this.ssoId;
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      sso_id: ssoId,
      pship_cc_key: rowData.PSHIP_CC_KEY,
      ptr_le_key: rowData.PTR_LE_KEY,
      acct_key: rowData.PSHIP_ACCT_KEY,
      tp_cc_key: rowData.TRADING_PARTNER,
      amount: rowData.OVERRIDE_ALLOCATION_AMT ? rowData.OVERRIDE_ALLOCATION_AMT : 0,
      pcnt: rowData.SA_PCT_OVERRIDE ? rowData.SA_PCT_OVERRIDE : 0,
      issue_key: rowData.ISSUE_KEY
    };
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=40xlbs`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleStirSdCodeSaveSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      leid: rowData.LEID,
      division: rowData.DIVISION,
      tax_year: rowData.TAX_YEAR || filterParams?.tax_year || globalContext?.globalParams?.tax_year,
      sd_code: rowData.SD_Code_0,
      is_issue_key_enabled: 'N',
      scenarioCode: 'RAF'
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=48f3i8`,
        payload,
        { withCredentials: true }
      ),
      'Success',
      'Error occurred while saving SD code'
    );
  }

  private handleStirSettlementEntityAdminSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      tax_year: rowData.TAX_YEAR || '',
      leid: rowData.LEID || '',
      cdr_no: rowData.CDR_NO || '',
      bsla_code: rowData.BSLA_CODE || '',
      le_type: rowData.LE_TYPE || '',
      in_out_code: rowData.IN_OUT_CODE || '',
      locked_entity: rowData.LOCKED_ENTITY || '',
      category: rowData.CATEGORY || '',
      is_issue_key_enabled: 'N',
      scenarioCode: 'RAF'
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=hrcv1p`,
        payload,
        { withCredentials: true }
      ),
      'Success',
      'Error occurred while saving settlement entity admin'
    );
  }

  private handleStirSettlementTargetDateSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colMap = data?.colData?.map;
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      object_id: rowData.object_id,
      target_date: colMap ? rowData?.[colMap] : rowData?.target_date,
      ...(colMap ? { [colMap]: rowData?.[colMap] } : {}),
      is_issue_key_enabled: 'N',
      scenarioCode: 'RAF'
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_id=33352`,
        payload,
        { withCredentials: true }
      ),
      'Success',
      'Error occurred while saving settlement target date'
    );
  }

  private handleSubmissionSignoffSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const payload = {
      ...globalContext?.globalParams,
      ...filterParams,
      combination_key: rowData.COMBINATION_KEY,
      sign_off: rowData.SIGNED_OFF,
      ti_amount: rowData.TOTAL_TI_LINE_30,
      ta_amount: rowData.TOTAL_TAX_ADJ
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=22l1fp`,
        payload,
        { withCredentials: true }
      ),
      'Data saved Successfully.',
      'Error occurred while saving submission signoff'
    );
  }

  private handleTbImportFromFdmAlertSave(data: any): Observable<any> {
    return this.wrapSave(
      this.firGridSaveService.tbImportFromFDMAlert(data),
      'Data saved Successfully.',
      'Validation failed for TB import alert'
    );
  }

  private handleDclSave(data: any, actionCode: string, processName: string): Observable<any> {
    const rowData = data?.rowData || {};
    const payload = {
      process_name: processName,
      clob_data: JSON.stringify([rowData]),
      clob_setting: JSON.stringify({ group_trans_key: '' })
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=${actionCode}`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private buildRelatedPartyPaymentPayload(data: any, processName: string, columnValue: string): any {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const colId = data?.col_id;
    const filingKey =
      filterParams?.filing_key ??
      globalContext?.globalParams?.filing_key ??
      rowData?.FILING_KEY ??
      '';
    const ssoId = this.ssoId;

    return {
      filing_key: filingKey,
      rp_seq_id: rowData?.RP_SEQ_ID,
      sso_id: ssoId,
      column_name: colId,
      column_value: columnValue,
      process_name: processName
    };
  }

  private handleScheduleToggleSave(
    data: any,
    config: {
      actionCode: string;
      processName: string;
      transTypeKey: string;
      lineNo: string;
      occurrence: string;
      lineAttribKey: string;
      attributeName: string;
      enabledFlag: string;
      transDetailsKey: string;
    }
  ): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const globalParams = globalContext?.globalParams || {};
    const taxYear = filterParams?.tax_year || globalParams?.tax_year || rowData?.TAX_YEAR;
    const scenario = filterParams?.scenario || globalParams?.scenario || rowData?.SCENARIO;
    const jcdKey = filterParams?.jcd_key || globalParams?.jcd_key || rowData?.JCD_KEY || 250;
    const enabledValue = Number(rowData?.[config.enabledFlag]) === 0 ? 'N' : 'Y';

    const editRow = [[{
      tax_year: taxYear,
      trans_type_key: rowData?.[config.transTypeKey],
      combination_key: rowData?.COMBINATION_KEY,
      group_obj_key: rowData?.GROUP_OBJ_KEY,
      form_key: rowData?.FORM_KEY,
      line_no: rowData?.[config.lineNo],
      occurence: String(rowData?.[config.occurrence]) === '0' ? null : rowData?.[config.occurrence],
      line_attrib_key: rowData?.[config.lineAttribKey],
      Attribute_Name: rowData?.[config.attributeName],
      Attribute_Value: enabledValue,
      trans_dtls_key: String(rowData?.[config.transDetailsKey]) === '0' ? null : rowData?.[config.transDetailsKey],
      trans_status: 'A'
    }]];

    const scheduleSettings = [{
      tax_year: taxYear,
      scenario,
      jcd_key: jcdKey
    }];
    const scheduleDetails = {
      addrows: [] as any[],
      editrows: editRow,
      removerows: [] as any[]
    };
    const payload = {
      ...globalParams,
      ...filterParams,
      jsonObj: JSON.stringify(scheduleDetails),
      jsonSettings: JSON.stringify(scheduleSettings),
      process_name: config.processName
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=${config.actionCode}`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleWwniSave(data: any, actionCode: string, processName: string): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const globalParams = globalContext?.globalParams || {};
    const ssoId = this.ssoId;
    const jsonSettings = [{
      tax_year: globalParams?.tax_year,
      scenario: globalParams?.scenario,
      jcd_key: globalParams?.jcd_key,
      process_name: processName,
      sso_id: ssoId
    }];
    const payload = {
      ...globalParams,
      ...filterParams,
      process_name: processName,
      sso_id: ssoId,
      jsonSettings: JSON.stringify(jsonSettings),
      jsonObj: JSON.stringify(rowData)
    };

    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=${actionCode}`,
        payload,
        { withCredentials: true }
      )
    );
  }

  private handleToggleChangeLogSave(data: any): Observable<any> {
    const rowData = data?.rowData || {};
    const payload = {
      tax_year: this.globalService?.global?.globalParams?.tax_year || rowData.TAX_YEAR || '',
      scenario: this.globalService?.global?.globalParams?.scenario || rowData.SCENARIO || '',
      client_key: rowData.CLIENT_KEY,
      jcd_key: this.globalService?.global?.globalParams?.jcd_key || rowData.JCD_KEY || 250,
      enable_disable_run: rowData.CHANGE_LOG_RUN_ENABLED
    };

    // Error feedback (snackbar) is handled globally by wrapSave. Rolling back
    // the optimistic checkbox change is handled by the component, which reloads
    // the grid on GridSaveFactoryService.gridSaveError$ — this admin gizmoGrid
    // renders outside the workspace activeScreen refresh mechanism.
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_id=32967`,
        payload,
        { withCredentials: true }
      ),
      'Succesfully updated client for change log run.',
      'Error occurred while updating client for change log run'
    );
  }

  private handleToggleBepsVarianceSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.http.post<any>(
        `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=6nchqj`,
        {
          sso_id: this.ssoId,
          tax_year: filterParams?.tax_year,
          scenario: filterParams?.scenario,
          activate: rowData.ACTIVATE === 1 ? 'Y' : 'N',
          variance_key: rowData.VARIANCE_KEY
        },
        { withCredentials: true }
      )
    );
  }

  private buildSaveContext(): { filterParams: any; globalContext: any } {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen;
    const filterParams =
      activeScreen?.filters?.getFilterParams && typeof activeScreen.filters.getFilterParams === 'function'
        ? activeScreen.filters.getFilterParams()
        : {};

    // Clone globalParams so we can safely augment with scenarioCode /
    // is_issue_key_enabled without mutating the shared GlobalService state.
    const baseGlobalParams = this.globalService.global?.globalParams || {};
    const globalParams: any = { ...baseGlobalParams };
    const inputs = this.globalService.global?.inputs || {};

    // Parity with JsonObjectFactoryService.saveJSON6 (shared): legacy backend
    // endpoints expect scenarioCode and is_issue_key_enabled in every save
    // payload. Resolve them from the active screen's scenario filter, or fall
    // back to tax_year -> scenarios lookup in GlobalService.inputs.
    try {
      const getFilters = activeScreen?.filters?.getFilters;
      if (typeof getFilters === 'function') {
        const filters = getFilters.call(activeScreen.filters) || [];
        const scenarioFilter = filters.find(
          (f: any) => f && f.param_name === 'scenario'
        );
        if (scenarioFilter && scenarioFilter.selected) {
          if (scenarioFilter.selected.CODE !== undefined) {
            globalParams.scenarioCode = scenarioFilter.selected.CODE;
          }
          if (scenarioFilter.selected.ISSUE_ID_ENABLED !== undefined) {
            globalParams.is_issue_key_enabled =
              scenarioFilter.selected.ISSUE_ID_ENABLED;
          }
        }
      } else if (globalParams.scenario) {
        const taxYearValues = inputs?.tax_year?.values || [];
        const tyBucket = taxYearValues.find(
          (i: any) => i && i.value == globalParams.tax_year
        );
        const scen = tyBucket?.scenarios?.find(
          (s: any) => s && s.scenario == globalParams.scenario
        );
        if (scen?.scenario_type_code !== undefined) {
          globalParams.scenarioCode = scen.scenario_type_code;
        }
        if (scen?.is_issue_key_enabled !== undefined) {
          globalParams.is_issue_key_enabled = scen.is_issue_key_enabled;
        }
      }
    } catch {
      // Defensive: never break save just because scenario lookup failed.
    }

    const globalContext = {
      globalParams,
      workspaceFactory: this.workspaceService.workspaceFactory,
      getScenario: (taxYear: any) => this.globalService.getScenario(taxYear),
      inputs,
    };

    return {
      filterParams: filterParams || {},
      globalContext,
    };
  }

  /**
   * Wraps a save HTTP call with unified success/error feedback.
   *
   * Handles BOTH backend error conventions so handlers don't have to:
   *  1. HTTP 200 with `callSuccess = 0` and an `errorMessage` in the body.
   *  2. A real HTTP error status (e.g. 403/500), where the message is carried
   *     on the HttpErrorResponse (`err.error`), e.g. "Can not perform this
   *     operation, because you do not have access to client resources".
   *
   * In both cases a red snackbar is shown exactly once and the error is
   * re-thrown so the caller (GridSaveFactoryService.saveSwitch) skips its
   * post-save callback.
   *
   * @param onError optional hook for handler-specific recovery (e.g. reverting
   *        a toggle and refreshing the grid). Runs once, after the snackbar.
   */
  private wrapSave(
    obs: Observable<any>,
    successMsg: string = 'Data saved Successfully.',
    errorMsg: string = 'Error occurred while saving',
    onError?: (err: any) => void
  ): Observable<any> {
    return obs.pipe(
      map((response: any) => {
        const callSuccess = response?.callSuccess;
        if (callSuccess === '1' || callSuccess === 1) {
          if (!response?.skipped) {
            this.gtwSnackbarService.openSnackBar(successMsg, 'gtw-snackbar--green');
          }
          return response;
        }

        // 200 + callSuccess=0: show the snackbar here and flag the error so the
        // catchError below does not show a second one.
        const msg = response?.errorMessage || errorMsg;
        this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--red');
        const handled: any = new Error(msg);
        handled.gtwSnackbarShown = true;
        throw handled;
      }),
      catchError((err: any) => {
        // Skip if the map() above already showed a snackbar for callSuccess=0.
        if (!err?.gtwSnackbarShown) {
          const apiMsg = this.extractSaveErrorMessage(err, errorMsg);
          this.gtwSnackbarService.openSnackBar(apiMsg, 'gtw-snackbar--red');
        }

        if (onError) {
          try {
            onError(err);
          } catch {
            // Never let recovery logic swallow/replace the original error.
          }
        }

        return throwError(err);
      })
    );
  }

  /**
   * Pulls a human-readable message out of whatever the save observable threw:
   * an HttpErrorResponse (raw string body or `{ errorMessage | error }`) or a
   * plain Error. Falls back to the handler-provided default.
   */
  private extractSaveErrorMessage(err: any, fallback: string): string {
    const httpBody = err?.error;
    if (typeof httpBody === 'string' && httpBody.trim()) {
      return httpBody;
    }
    if (httpBody && typeof httpBody === 'object') {
      const fromBody = httpBody.errorMessage || httpBody.error || httpBody.message;
      if (fromBody) {
        return fromBody;
      }
    }
    return err?.errorMessage || err?.message || fallback;
  }

  private refreshActiveScreenGrid(): void {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen;
    if (activeScreen?.screen_key) {
      this.workspaceService.refreshGridData.next(activeScreen);
    }
  }

  private handleGhostRunStatusSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.ghostSystemRunStatusService.saveGhostRunStatus(
        this.GENERAL_CONFIG.base_url,
        data,
        globalContext,
        filterParams
      ),
      'Data saved Successfully.',
      'Error!!! Ghost System Status is not updated'
    );
  }

  private handleGhostStatusSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.ghostSystemStatusService.saveGhostStatus(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'Ghost System Status saved Successfully.',
      'Error saving Ghost System Status'
    );
  }

  private handleGhostOwnershipSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.ghostOwnershipInfoService.saveGhostOwnership(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'Ghost Ownership saved Successfully.',
      'Error saving Ghost Ownership'
    );
  }

  private handleUpdateGhostSettlement(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const oldRowData = data?.oldRowData || {};
    const colId = data?.col_id || data?.colData?.map || '';

    // Legacy parity: skip save if value unchanged
    if (colId && rowData[colId] === oldRowData[colId]) {
      return of({ callSuccess: '1', skipped: true });
    }

    return this.wrapSave(
      this.dcsGhostSettlementService.updateGhostSettlement(
        this.GENERAL_CONFIG.base_url,
        data,
        [rowData],
        globalContext?.globalParams || {},
        filterParams,
        rowData
      ),
      'Ghost Settlement updated Successfully.',
      'Error updating Ghost Settlement'
    );
  }

  private handleSubPartFSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.subPartFService.saveSubPartF(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'Sub Part F saved Successfully.',
      'Error saving Sub Part F'
    );
  }

  private handleSubPartFDetailsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.subPartFService.saveSubPartFDetails(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'Sub Part F details saved Successfully.',
      'Error saving Sub Part F details'
    );
  }

  private handleHtieNewElectionSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.firGridSaveService.htieNewElectionSave(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'HTIE Election saved Successfully.',
      'Error saving HTIE Election'
    );
  }

  private handleHtiePassiveNewElectionSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.firGridSaveService.htiePassiveNewElectionSave(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'HTIE Passive Election saved Successfully.',
      'Error saving HTIE Passive Election'
    );
  }

  private handleQbaiExternalAmtsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.firGiltiQbaiService.saveqbaiExternalAmts(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'QBAI External Amounts saved Successfully.',
      'Error saving QBAI External Amounts'
    );
  }

  private handleExternalQuarterAmountsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const ssoId = this.ssoId;
    return this.wrapSave(
      this.firGiltiQbaiService.saveExternalQuarterAmounts(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams, ssoId
      ),
      'External Quarter Amounts saved Successfully.',
      'Error saving External Quarter Amounts'
    );
  }

  private handle163jLineAmountsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.fir163jSummaryLeMeService.save163jLineAmounts(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      '163j Line Amounts saved Successfully.',
      'Error saving 163j Line Amounts'
    );
  }

  private handle163jEnableSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.fir163jSummaryHoService.save163jEnable(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      '163j Enable flag saved Successfully.',
      'Error saving 163j Enable flag'
    );
  }

  private handleScheduleGSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const globalParams = globalContext?.globalParams || {};
    const rowData = data?.rowData || {};
    const colId = data?.col_id || data?.colData?.map || '';
    const taxYear = filterParams?.tax_year || globalParams.tax_year || rowData.TAX_YEAR;

    if (parseInt(taxYear, 10) < 2020) {
      const jsonObj = [{
        ho_combination_key: rowData.HO_COMBINATION_KEY,
        ho_leid: rowData.LEID,
        ho_cdr_no: rowData.CDR_NO,
        ho_reporting_period: rowData.REPORTING_PERIOD,
        job_id: rowData.JOB_ID,
        filing_key: rowData.FILING_KEY,
        adj_amt: rowData[colId],
        column_name: colId
      }];
      const jsonSettings = [{ tax_year: rowData.TAX_YEAR }];
      return this.wrapSave(
        this.form5471TextinfoService.saveScheduleGInline(
          this.GENERAL_CONFIG.base_url, 'chgxp7', jsonObj, jsonSettings,
          'save_schedule_G', globalParams, filterParams
        ),
        'Changes have been saved',
        'Error saving Schedule G'
      );
    }

    const columnMap: Record<string, string> = {
      DM_ITMS_AMT: 'DM', HT_ITMS_AMT: 'HT', DED_ITMS_AMT: 'DED',
      AHC_ITMS_AMT: 'AHC', BN_ITMS_AMT: 'BN', ARR_ITMS_AMT: 'ARR',
      RD_ITMS_AMT: 'RD', SD_ITMS_AMT: 'SD', SCDI_ITMS_AMT: 'SCDI',
      SCRR_ITMS_AMT: 'SCRR', LT_ITMS_AMT: 'LT', AC_ITMS_AMT: 'AC',
      SCM_ITMS_AMT: 'SCM', SCSU_ITMS_AMT: 'SCSU', PM_ITMS_AMT: 'PM',
      SC_ITMS_AMT: 'SC', BR_ITMS_AMT: 'BR', AF_ITMS_AMT: 'AF',
      EF_ITMS_AMT: 'EF', AI_ITMS_AMT: 'AI', EP_ITMS_AMT: 'EP',
      XX_ITMS_AMT: 'XX'
    };
    const prefix = columnMap[colId];
    if (!prefix) {
      this.gtwSnackbarService.openSnackBar('Unknown column for Schedule G save', 'gtw-snackbar--red');
      return of({ callSuccess: '0', skipped: true });
    }

    if (colId === 'DED_ITMS_AMT' && rowData.DM_ITMS_AMT != null && rowData.DM_ITMS_AMT !== '') {
      this.gtwSnackbarService.openSnackBar(
        'Cannot enter Deduction taken into account(3 DED), as De Minimis(1 DM) value is not null.',
        'gtw-snackbar--red'
      );
      return of({ callSuccess: '0', skipped: true });
    }

    const lineAttribKey = rowData[`${prefix}_LINE_ATTRIB_KEY`] || 0;
    const attribName = rowData[`${prefix}_ATTRIB_NAME`] || '';
    const transDetailsKey = rowData[`${prefix}_TRANS_DETAILS_KEY`] !== undefined
      ? rowData[`${prefix}_TRANS_DETAILS_KEY`] : 0;
    const rawAmt = rowData[colId];
    const amt = (rawAmt === '' || rawAmt == null) ? null : parseInt(rawAmt, 10);

    const transTypeKey = parseInt(taxYear, 10) < 2020 ? 28 : 1229;
    const returnObj = [{
      tax_year: taxYear,
      trans_type_key: transTypeKey,
      combination_key: rowData.HO_COMBINATION_KEY,
      group_obj_key: rowData.GROUP_OBJ_KEY,
      form_key: rowData.FORM_KEY,
      line_no: '1',
      occurence: 1,
      line_attrib_key: lineAttribKey,
      Attribute_Name: attribName,
      Attribute_Value: amt,
      trans_dtls_key: transDetailsKey,
      trans_status: 'A'
    }];

    const editRow: any[][] = [];
    const removeRow: any[][] = [];
    if (amt != null) {
      editRow.push(returnObj);
    } else {
      removeRow.push(returnObj);
    }

    if (colId === 'DM_ITMS_AMT' && rowData.DM_ITMS_AMT != null && rowData.DM_ITMS_AMT !== '' &&
        (rowData.DED_ITMS_AMT != null || rowData.DED_ITMS_AMT !== undefined)) {
      const dedRemoveObj = [{
        tax_year: taxYear,
        trans_type_key: transTypeKey,
        combination_key: rowData.HO_COMBINATION_KEY,
        group_obj_key: rowData.GROUP_OBJ_KEY,
        form_key: rowData.FORM_KEY,
        line_no: '1',
        occurence: 1,
        line_attrib_key: rowData.DED_LINE_ATTRIB_KEY,
        Attribute_Name: rowData.DED_ATTRIB_NAME,
        Attribute_Value: null,
        trans_dtls_key: rowData.DED_TRANS_DETAILS_KEY,
        trans_status: 'A'
      }];
      removeRow.push(dedRemoveObj);
    }

    const jsonObj = { addrows: [], editrows: editRow, removerows: removeRow };
    const jcdKey = (globalParams.jcd_key === '' || globalParams.jcd_key === undefined)
      ? '250' : globalParams.jcd_key;
    const scenario = filterParams?.scenario || globalParams.scenario;
    const jsonSettings = [{ tax_year: taxYear, scenario, jcd_key: jcdKey }];

    return this.wrapSave(
      this.form5471TextinfoService.saveScheduleGInline(
        this.GENERAL_CONFIG.base_url, 'fjaaz7', jsonObj, jsonSettings,
        'save_5471_G19', globalParams, filterParams
      ),
      'Data saved Successfully.',
      'Error saving Schedule G'
    );
  }

  private handleSchedule5471SchdESave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const oldRowData = data?.oldRowData || {};
    const colId = data?.col_id;
    const globalParams = globalContext?.globalParams || {};
    const taxYear = filterParams?.tax_year;
    const jcdKey = (globalParams.jcd_key === '' || globalParams.jcd_key == null) ? '250' : globalParams.jcd_key;
    const jsonSettings = [{ tax_year: taxYear, scenario: filterParams?.scenario, jcd_key: jcdKey }];

    let editRow: any[];
    if (colId === 'COL_G') {
      editRow = [[{
        tax_year: taxYear,
        trans_type_key: 28,
        combination_key: rowData.COMBINATION_KEY,
        group_obj_key: rowData.GROUP_OBJ_KEY,
        form_key: rowData.FORM_KEY,
        line_no: rowData.LINE_NO,
        occurence: rowData.OCCURRENCE,
        line_attrib_key: rowData.CHECKBOX_LINE_ATTRIB_KEY,
        Attribute_Name: rowData.CHECKBOX_ATTRIB_NAME,
        Attribute_Value: rowData.COL_G,
        override_y_n: rowData.CHECKBOX_OVERRIDE_Y_N,
        trans_dtls_key: (rowData.CHECKBOX_TRANS_DETAILS_KEY == 0) ? null : rowData.CHECKBOX_TRANS_DETAILS_KEY,
        trans_status: 'A'
      }]];
    } else {
      const overrideYN = (oldRowData.COL_F !== rowData.COL_F) ? 'Y' : rowData.AMT_OVERRIDE_Y_N;
      editRow = [[{
        tax_year: taxYear,
        trans_type_key: 28,
        combination_key: rowData.COMBINATION_KEY,
        group_obj_key: rowData.GROUP_OBJ_KEY,
        form_key: rowData.FORM_KEY,
        line_no: rowData.LINE_NO,
        occurence: rowData.OCCURRENCE,
        line_attrib_key: rowData.AMT_LINE_ATTRIB_KEY,
        Attribute_Name: rowData.AMT_ATTRIB_NAME,
        Attribute_Value: rowData.COL_F,
        override_y_n: overrideYN,
        trans_dtls_key: (rowData.AMT_TRANS_DETAILS_KEY == 0) ? null : rowData.AMT_TRANS_DETAILS_KEY,
        trans_status: 'A'
      }]];
    }

    const payload = {
      jsonSettings,
      jsonObj: { addrows: [] as any[], editrows: editRow, removerows: [] as any[] }
    };
    return this.wrapSave(
      this.fir5471ScheduleEService.saveSchedule5471SchdE(
        this.GENERAL_CONFIG.base_url, payload, globalContext, filterParams, rowData
      ),
      '5471 Schedule E saved Successfully.',
      'Error saving 5471 Schedule E'
    );
  }

  private handleFirReviewAutoCheckboxSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.firReviewAutomationService.updateFirTBAutomation(
        this.GENERAL_CONFIG.base_url, rowData, globalContext, filterParams
      ),
      'FIR Review Automation saved Successfully.',
      'Error saving FIR Review Automation'
    );
  }

  private handleFirDiagnosticsActionSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.firDiagnosticsService.saveFIRDiagnosticsAction(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'FIR Diagnostics saved Successfully.',
      'Error saving FIR Diagnostics'
    );
  }

  private handleAllocationAmountsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.partnerAllocationsService.saveAllocationAmounts(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'Allocation Amounts saved Successfully.',
      'Error saving Allocation Amounts'
    );
  }

  private handleSourcingAmountsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    const gp = globalContext.globalParams || {};

    const taxYear = Number(filterParams?.tax_year ?? gp?.tax_year ?? 0);
    const scenario = Number(filterParams?.scenario ?? gp?.scenario ?? 0);
    const jcdKey = Number(gp?.jcd_key ?? filterParams?.jcd_key ?? 250);
    const ssoId = gp?.sso_id || '';
    const combinationKey = Number(rowData?.COMBINATION_KEY ?? 0);

    return this.wrapSave(
      this.sourcingAmountsService.saveSourcingAmounts(
        this.GENERAL_CONFIG.base_url,
        taxYear,
        scenario,
        jcdKey,
        ssoId,
        'SOURCING_AMOUNTS_SAVE',
        [],
        filterParams?.scenarioCode || 'RAF',
        'N',
        0,
        { tax_year: taxYear, scenario, jcd_key: jcdKey, sso_id: ssoId, combination_key: combinationKey },
        [rowData]
      ),
      'Sourcing Amounts saved Successfully.',
      'Error saving Sourcing Amounts'
    );
  }

  private handleCapitalAmountsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.partnershipCapitalAccountsService.saveCapitalAmounts(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'Capital Amounts saved Successfully.',
      'Error saving Capital Amounts'
    );
  }

  private handleCapitalAmountsTaxSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.partnershipCapitalAccountsTaxService.saveCapitalAmountsTax(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'Capital Amounts (Tax) saved Successfully.',
      'Error saving Capital Amounts (Tax)'
    );
  }

  private handleM2AmountsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData || {};
    return this.wrapSave(
      this.form1065M2Service.saveM2Amounts(
        this.GENERAL_CONFIG.base_url, data, [rowData], globalContext, rowData, filterParams
      ),
      'M2 Amounts saved Successfully.',
      'Error saving M2 Amounts'
    );
  }

  private handleM2TaxAmountsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.partnershipM2TaxService.saveM2TaxAmounts(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'M2 Tax Amounts saved Successfully.',
      'Error saving M2 Tax Amounts'
    );
  }

  private handleWwniM3CommentsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.wwniM3ReconService.saveWWNIM3Comments(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'WWNI M3 comment saved Successfully.',
      'Error saving WWNI M3 comment'
    );
  }

  private handleLockFormSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.lockFormService.savelockForm(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'Form Lock saved Successfully.',
      'Error saving Form Lock'
    );
  }

  private handleJobDisplaySave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.jobDisplayService.saveJobDisplay(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'Job Display saved Successfully.',
      'Error saving Job Display'
    );
  }

  private handleLocalPurchaseAmountSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.adjOpeningReserveBalancesService.saveLocalPurchaseAdj(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'Local Purchase Adjustment saved Successfully.',
      'Error saving Local Purchase Adjustment'
    );
  }

  private handle1125AmountSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.form1125aService.save1125ADetails(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'Form 1125-A saved Successfully.',
      'Error saving Form 1125-A'
    ).pipe(
      tap((response) => {
        if (!response?.skipped) {
          this.refreshActiveScreenGrid();
        }
      })
    );
  }

  private handleEstSignOffSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.estSignOffService.saveSignOff(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'EST Sign Off saved Successfully.',
      'Error saving EST Sign Off'
    );
  }

  private handleDcsDiagnosticsSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    return this.wrapSave(
      this.dcsDiagnosticsService.save_DCS_Diagnostics(
        this.GENERAL_CONFIG.base_url, data, globalContext, filterParams
      ),
      'DCS Diagnostics saved Successfully.',
      'Error saving DCS Diagnostics'
    );
  }

  private handleEntityLockSave(data: any): Observable<any> {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    const screenKey = activeScreen?.screen_key;
    const rowData = data?.rowData;
    if (!rowData) return new Observable(sub => sub.error('No row data'));

    const lockValue = rowData.LOCK_Y_N == 1 ? 'Y' : 'N';
    let jsonObj: any = {};

    const screenKeyStr = String(screenKey);
    switch (screenKeyStr) {
      case '10742':
        jsonObj = { me_leid: rowData.ME_CODE, me_leid_name: rowData.ME_NAME, lock_y_n: lockValue, class_type: 'business', cache_key: 'GET_BUSINESS_LOCKED_ENTITIES' };
        break;
      case '10738':
        jsonObj = { me_leid: rowData.HO_LEID, me_leid_name: rowData.HO_LE_NAME, consol_group_key: rowData.CONSOL_GROUP_KEY, rpt_period: rowData.HO_REPORTING_PERIOD, lock_y_n: lockValue, class_type: 'entity', cache_key: 'GET_LOCKED_LEIDS' };
        break;
      case '11118':
        jsonObj = { me_leid: rowData.ME_CODE, me_leid_name: rowData.ME_NAME, lock_y_n: lockValue, class_type: 'ghost' };
        break;
      case '10743':
        jsonObj = { me_leid: rowData.CONSOL_GROUP_KEY, me_leid_name: rowData.GROUP_NAME, lock_y_n: lockValue, class_type: 'consol', cache_key: 'GET_CONSOL_LOCK_ENTITIES' };
        break;
      case '10769':
        jsonObj = { me_leid: rowData.SOURCE_SYSTEM_FLAG, me_leid_name: rowData.SOURCE_SYSTEM_FLAG_NAME, lock_y_n: lockValue, class_type: 'domain' };
        break;
      case '11133':
        jsonObj = { me_leid: rowData.CONSOL_GROUP_KEY, me_leid_name: rowData.GROUP_NAME, lock_y_n: lockValue, class_type: 'partnership' };
        break;
      case '11413':
        jsonObj = { me_leid: rowData.CONSOL_GROUP_KEY, me_leid_name: rowData.GROUP_NAME, lock_y_n: lockValue, class_type: 'WWNI' };
        break;
      case '11614':
        jsonObj = { me_leid: rowData.CONSOL_GROUP_KEY, me_leid_name: rowData.GROUP_NAME, lock_y_n: lockValue, class_type: 'partnership_credit' };
        break;
    }

    const { filterParams, globalContext } = this.buildSaveContext();
    const globalParams = globalContext?.globalParams || {};
    const scenario = filterParams?.scenario ?? globalParams?.scenario;
    const params: any = {
      tax_year: filterParams.tax_year,
      scenario,
      jcd_key:
        filterParams?.jcd_key ??
        globalParams?.jcd_key ??
        this.globalService.global?.globalParams?.jcd_key,
      parent_me_string: filterParams.parent_me_string,
      lockedjsonObj: JSON.stringify(jsonObj),
      issue_id: globalParams?.issue_id ?? '',
      issue_short_desc: globalParams?.issue_short_desc ?? '',
      checkLockedbj: JSON.stringify([{ scenario_key: Number(scenario) || 0 }]),
      scenarioCode:
        filterParams?.scenarioCode ??
        filterParams?.scenario_code ??
        globalParams?.scenarioCode ??
        'RAF',
      is_issue_key_enabled:
        filterParams?.is_issue_key_enabled ??
        globalParams?.is_issue_key_enabled ??
        'N',
    };

    return this.http
      .post<any>(this.GENERAL_CONFIG.base_url + '/saveEntityBusinessConsolLock', params)
      .pipe(
        take(1),
        map((response: any) => {
          if (response.callSuccess == '1') {
            this.gtwSnackbarService.openSnackBar('Entity Lock saved Successfully', 'gtw-snackbar--green');
            if (jsonObj.cache_key) {
              this.refreshCacheObjectByKey(jsonObj.cache_key, params.tax_year, params.scenario, params.jcd_key);
            }
            return response;
          }
          throw new Error(response.errorMessage || 'Save failed');
        }),
        catchError((err: any) => {
          const msg = err?.message || err?.errorMessage || 'Error saving entity lock';
          this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--red');
          return throwError(err);
        })
      );
  }

  private handleMaxLoanSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const row = data?.rowData;
    if (!row) return new Observable(sub => sub.error('No row data'));

    if (row.USER_AMT === null || row.USER_AMT === undefined || row.USER_AMT === '') {
      this.gtwSnackbarService.openSnackBar('Please enter a value for User Amt', 'gtw-snackbar--yellow');
      return new Observable(sub => sub.next({ skipped: true }));
    }

    const tempObj = {
      COMBINATION_KEY: row.COMBINATION_KEY,
      TRANS_DETAILS_KEY: row.TRANS_DETAILS_KEY || 0,
      TRANS_HEADER_KEY: row.TRANS_HEADER_KEY || 0,
      ACCT_KEY: row.LOCAL_ACCT_KEY,
      TP_KEY: row.TP_KEY,
      CHART_KEY: row.LOCAL_CHART_KEY,
      USER_AMT: row.USER_AMT,
      ISSUE_KEY: row.ISSUE_KEY
    };
    const jsonSettings = JSON.stringify([{ tax_year: filterParams?.tax_year }]);
    const jsonObj = JSON.stringify([tempObj]);

    const params = _.merge({}, globalContext?.globalParams || {}, filterParams, {
      jsonSettings,
      jsonObj,
      process_name: 'saveMaxLoanDetails'
    });

    return this.wrapSave(
      this.http
        .post<any>(this.GENERAL_CONFIG.base_url + '/saveJsonObject?action_code=qx3meb', params)
        .pipe(take(1)),
      'Data saved Successfully.',
      'Error saving Max Loan data'
    );
  }

  private handle901AdjustmentSave(data: any): Observable<any> {
    const { filterParams, globalContext } = this.buildSaveContext();
    const rowData = data?.rowData;
    const colData = data?.colData;
    if (!rowData) return new Observable(sub => sub.error('No row data'));

    return this.dashboard901MService
      .save901Adjustment(
        this.GENERAL_CONFIG.base_url,
        rowData,
        colData,
        data?.COMMENTS || '',
        globalContext,
        filterParams
      )
      .pipe(
        map((response: any) => {
          if (response?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Data saved Successfully.', 'gtw-snackbar--green');
            const activeScreen = this.workspaceService.workspaceFactory?.activeScreen;
            if (activeScreen) {
              this.workspaceService.refreshGridData.next(activeScreen);
            }
            return response;
          }
          const msg = response?.errorMessage || 'Error saving 901(m) adjustment';
          this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--red');
          throw new Error(msg);
        })
      );
  }

  private refreshCacheObjectByKey(cacheKey: string, taxYear: string, scenario: string, jcdKey: string): void {
    // Legacy used GENERAL_CONFIG.common_api_url which maps to '/batch' (batch_api_url).
    // Using base_url produced an incorrect path; switch to batch_api_url to match.
    const batchUrl = (this.GENERAL_CONFIG as any).batch_api_url || this.GENERAL_CONFIG.base_url;
    this.http
      .post<any>(batchUrl + '/refreshCachedObjectByKey', {
        objectKey: cacheKey,
        tax_year: taxYear,
        scenario: scenario,
        jcd_key: jcdKey
      })
      .pipe(take(1))
      .subscribe();
  }
}
